import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../config/api";
import { featureFlags } from "../../config/featureFlags";
import { useAuth } from "../../components/AuthProvider";
import { useI18n } from "../../i18n";
import ccStyles from "../../styles/pages/compliance-center/ComplianceCenterOverview.module.scss";
import { getStandardDisplayName, normalizeComplianceStandardCode, standardsEquivalent } from "../../utils/complianceStandard";
import { isControlCentricUxEnabledForStandard } from "../../utils/controlCentricUx";
import {
  computeJourneyWorkbenchStats,
  deriveEvidenceBucket,
  deriveGdprAssessmentTone,
  formatHumanWorkflowSessionOptionLabel,
  formatIsoDate,
  GDPR_ENTERPRISE_COMMON_PROBLEMS,
  inferActionTypes,
  inheritanceCompactBlurb,
  inheritanceRelationshipTone,
  journeyEvidenceWarnings,
  limitRecommendationParagraphs,
  matchRecommendationsAgainstRiskSet,
  parseRemediationChecklistItems,
  parseSemicolonBulletList,
  resolveJourneyQuestionRowPresentation,
  type GdprEnterpriseTone,
  type RecommendationRiskMatchMode,
} from "./journeyWorkbenchHelpers";
import {
  journeyResumeStorageKey,
  readJourneyResumePointer,
  resolveFlatJourneyResumeIndex,
  writeJourneyResumePointer,
} from "./journeyResumePosition";
import { Button } from "../../components/ui/Button";
import compliancePageStyles from "../../styles/pages/Compliance.module.css";
import { Nis2EnterpriseQuestionWorkbench } from "./Nis2EnterpriseQuestionWorkbench";
import { Soc2EnterpriseQuestionWorkbench } from "./Soc2EnterpriseQuestionWorkbench";
import { QuestionEvidenceBindingPanel } from "../../components/compliance-workflow/questions/QuestionEvidenceBindingPanel";
import { ControlCentricWorkflowWorkspace } from "../../components/compliance-workflow/control-centric/ControlCentricWorkflowWorkspace";
import { JourneyCompactMetricsBar } from "../../components/compliance-workflow/control-centric/JourneyCompactMetricsBar";
import type { ControlCentricOverview } from "../../services/controlCentricWorkflowApi";
import listStyles from "../../styles/pages/governance/GovernanceListing.module.scss";
import { parseRemediationJourneySearch } from "./journeyRemediationNav";
import { continueRemediationSession, fetchUnresolvedControls } from "../../services/complianceLifecycleApi";

/** Match governance “Add Evidence” primary (green). */
const JOURNEY_PRIMARY_BTN = "!bg-green-600 hover:!bg-green-700 !border-green-600";

type Lang = "en" | "ru" | "es";

type LocTriple = { en?: string; ru?: string; es?: string };

/** Prisma JSON often uses per-locale blocks `{ title, description }` instead of flat strings. */
function stringifyLocLeaf(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    const o = value as Record<string, unknown>;
    const title = typeof o.title === "string" ? o.title : "";
    const description = typeof o.description === "string" ? o.description : "";
    const objective = typeof o.objective === "string" ? o.objective : "";
    if (title && description) return `${title} — ${description}`;
    if (title && objective) return `${title} — ${objective}`;
    return title || description || objective || "";
  }
  return String(value);
}

type ArticleTraceabilityBlock = {
  primaryArticle: string;
  secondaryArticles: string[];
  operationalObligationSummary: LocTriple;
  regulatorConcernSummary: LocTriple;
  auditFocusAreas: LocTriple;
};

type CatalogQuestion = {
  questionKey: string;
  canonicalControlCode: string;
  localization: LocTriple;
  evidenceHint?: LocTriple;
  recommendationHint?: LocTriple;
  remediationHint?: LocTriple;
  ownerRoleKey?: string;
  readinessImpact?: number;
  evidenceModel?: Record<string, unknown>;
  articleRefs?: string[];
  articleTraceability?: ArticleTraceabilityBlock;
  whyThisMatters?: LocTriple;
  deterministicRulesLocalized?: LocTriple;
  inheritanceMappingHints?: LocTriple;
  auditorWarnings?: LocTriple;
  explainabilitySignals?: string[];
  reviewCadence?: string;
  reviewCadenceLocalized?: LocTriple;
  auditorGuidance?: LocTriple;
  suggestedArtifactsLocalized?: LocTriple;
};

const GDPR_GE = "complianceCenterUi.journey.gdprEnterprise";
const NIS2_GE = "complianceCenterUi.journey.nis2Enterprise";
const SOC2_GE = "complianceCenterUi.journey.soc2Enterprise";

function gdprToneExecutiveClass(tone: GdprEnterpriseTone): string {
  if (tone === "green") return ccStyles.gdprExecutiveValueGreen;
  if (tone === "red") return ccStyles.gdprExecutiveValueRed;
  return ccStyles.gdprExecutiveValueAmber;
}

function gdprToneAssessmentClass(tone: GdprEnterpriseTone): string {
  if (tone === "green") return ccStyles.gdprAssessmentGreen;
  if (tone === "red") return ccStyles.gdprAssessmentRed;
  return ccStyles.gdprAssessmentAmber;
}

function gdprToneInheritanceBadgeClass(tone: GdprEnterpriseTone): string {
  if (tone === "green") return ccStyles.gdprInheritanceBadgeGreen;
  if (tone === "red") return ccStyles.gdprInheritanceBadgeRed;
  return ccStyles.gdprInheritanceBadgeAmber;
}

function GdprEvidenceChips({ items }: { items: string[] }) {
  if (!items.length) return <p className="text-xs text-slate-500">—</p>;
  return (
    <div className={ccStyles.gdprEvidenceChipList}>
      {items.map((item) => (
        <span key={item} className={ccStyles.gdprEvidenceChip}>
          {item}
        </span>
      ))}
    </div>
  );
}

type CatalogDomain = {
  key: string;
  sortOrder: number;
  localization: LocTriple;
  questions: CatalogQuestion[];
  curatedScenarioRecommendations?: Array<{ id: string; title: LocTriple; detail: LocTriple }>;
};

type PackQuestion = {
  questionKey: string;
  canonicalControlCode: string;
  localization?: {
    en?: { questionText?: string; auditorGuidance?: string; evidenceCollectionGuidance?: string; ownershipGuidance?: string };
    ru?: { questionText?: string; auditorGuidance?: string; evidenceCollectionGuidance?: string; ownershipGuidance?: string };
    es?: { questionText?: string; auditorGuidance?: string; evidenceCollectionGuidance?: string; ownershipGuidance?: string };
  };
  recommendationLocalization?: { en?: { text?: string }; ru?: { text?: string }; es?: { text?: string } };
  remediationLocalization?: { en?: { steps?: string[] }; ru?: { steps?: string[] }; es?: { steps?: string[] } };
  evidenceExpectations?: { artifactTypes?: string[]; linkedStandards?: string[] };
  evidenceRuleLocalization?: { en?: { title?: string; body?: string }; ru?: { title?: string; body?: string }; es?: { title?: string; body?: string } };
  linkedStandards?: string[];
};

type PackDomain = {
  domainKey: string;
  sortOrder: number;
  questions: PackQuestion[];
  curatedScenarioRecommendations?: Array<{ id: string; title: LocTriple; detail: LocTriple }>;
};

type CanonicalControlRow = {
  code: string;
  localization?: Record<string, { title?: string; objective?: string }>;
  mappings?: Array<{ standardCode: string; externalRef?: string }>;
  evidenceRequirements?: Array<{ requirementKey?: string; required?: boolean; localization?: LocTriple }>;
  metadata?: {
    inheritedMappings?: Array<{
      targetStandard: string;
      relationship?: string;
      inheritanceConfidence?: string;
      limitations?: LocTriple;
      auditorWarning?: LocTriple;
      prohibitedAssumptions?: LocTriple;
      residualRequirements?: LocTriple;
      mappingRationale?: LocTriple;
      reviewGuidance?: LocTriple;
    }>;
  };
};

type ProjectionRow = {
  status?: string;
  readinessScore?: number;
  evidenceCompleteness?: number;
  control?: CanonicalControlRow;
};

const JOURNEY_STANDARD_CODES = [
  "ISO27001",
  "ISO42001",
  "GDPR",
  "DORA",
  "NIS2",
  "CIS",
  "SOC2",
  "PCI_DSS_2025",
  "SWIFT_CSCF_2025",
  "ENS_ES",
  "ENS_ANSSI",
  "BSI_DE",
  "KSBP_LT",
  "NCA_EE",
  "SDLC_SECURITY",
  "TECH_CONTROLS",
] as const;

function isJourneyStandardCode(code: string): boolean {
  return (JOURNEY_STANDARD_CODES as readonly string[]).includes(code);
}

function resolveJourneyStandardFromStorage(): string {
  const n = normalizeComplianceStandardCode(localStorage.getItem("ccSelectedStandard"));
  if (n !== "ALL" && isJourneyStandardCode(n)) return n;
  return "ISO27001";
}

function pickLoc(loc: LocTriple | Record<string, unknown> | undefined, lang: Lang): string {
  if (!loc) return "";
  const L = loc as Record<string, unknown>;
  if (lang === "ru" && L.ru !== undefined && L.ru !== null) {
    const s = stringifyLocLeaf(L.ru);
    if (s) return s;
  }
  if (lang === "es" && L.es !== undefined && L.es !== null) {
    const s = stringifyLocLeaf(L.es);
    if (s) return s;
  }
  const chain = [stringifyLocLeaf(L.en), stringifyLocLeaf(L.ru), stringifyLocLeaf(L.es)].filter(Boolean);
  return chain[0] || "";
}

function pickPackLoc(
  pq: PackQuestion | undefined,
  lang: Lang,
  field: "questionText" | "auditorGuidance" | "evidenceCollectionGuidance" | "ownershipGuidance",
): string {
  const L = pq?.localization;
  if (!L) return "";
  const block = lang === "ru" ? L.ru : lang === "es" ? L.es : L.en;
  const v = block?.[field];
  return typeof v === "string" ? v : "";
}

function mapAnswerToApi(choice: string): string {
  const c = String(choice || "").toUpperCase();
  if (c === "YES") return "yes";
  if (c === "PARTIAL") return "partial";
  if (c === "NO") return "no";
  if (c === "NOT_APPLICABLE" || c === "N/A") return "n/a";
  return String(choice || "").toLowerCase();
}

/** Snapshot for Assessments topic grid (scroll targets + copy). */
export type OperationalJourneyWorkspaceSummary = {
  journeyStandard: string;
  sessionId: string | null;
  totalQ: number;
  answered: number;
  unanswered: number;
  evidenceMissingAnswers: number;
  evidenceLinkedAnswers: number;
  compliantProjections: number;
  partialProjections: number;
  nonCompliantProjections: number;
  pendingEvidenceProjections: number;
  domainAnswered: number;
  domainTotal: number;
  domainMissingCount: number;
  /** Display count for topic cards (matched recs, or total when API rows lack control identifiers). */
  recommendationsForRiskControls: number;
  recommendationMatchMode: RecommendationRiskMatchMode;
  recommendationsApiTotal: number;
};

type OperationalComplianceJourneyWorkspaceProps = {
  enabledStandards: string[];
  /** When set (e.g. from Overview), pre-select if present in journey list */
  preferredStandard?: string;
  navigationMode: "SEQUENTIAL" | "FREE";
  onNavigationModeChange: (mode: "SEQUENTIAL" | "FREE") => void;
  /** Fired when session/catalog-derived stats change (parent topic cards). */
  onWorkspaceSummaryChange?: (summary: OperationalJourneyWorkspaceSummary | null) => void;
};

export default function OperationalComplianceJourneyWorkspace({
  enabledStandards,
  preferredStandard,
  navigationMode: navMode,
  onNavigationModeChange: setNavMode,
  onWorkspaceSummaryChange,
}: OperationalComplianceJourneyWorkspaceProps) {
  const { token } = useAuth();
  const { t, language } = useI18n();
  const lang = (language === "ru" || language === "es" ? language : "en") as Lang;

  const [catalog, setCatalog] = useState<CatalogDomain[]>([]);
  const [packs, setPacks] = useState<PackDomain[]>([]);
  const [controlsByCode, setControlsByCode] = useState<Record<string, CanonicalControlRow>>({});
  const [projections, setProjections] = useState<ProjectionRow[]>([]);
  const [crosswalkByControl, setCrosswalkByControl] = useState<Record<string, Array<{ standardCode: string; externalRef?: string }>>>({});
  const [apiRecommendations, setApiRecommendations] = useState<any[]>([]);

  const [journeyStandard, setJourneyStandard] = useState<string>(() => {
    const pref = normalizeComplianceStandardCode(preferredStandard || "");
    if (pref && pref !== "ALL" && isJourneyStandardCode(pref)) return pref;
    return resolveJourneyStandardFromStorage();
  });
  const [session, setSession] = useState<any | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [controlCentricOverview, setControlCentricOverview] = useState<ControlCentricOverview | null>(null);
  const [controlCentricSessionCompleted, setControlCentricSessionCompleted] = useState(false);
  const [sessionsList, setSessionsList] = useState<any[]>([]);

  const [selectedDomainKey, setSelectedDomainKey] = useState<string | null>(null);
  const [selectedQuestionKey, setSelectedQuestionKey] = useState<string | null>(null);

  const [answerChoice, setAnswerChoice] = useState<string>("");
  const [businessOwner, setBusinessOwner] = useState("");
  const [technicalOwner, setTechnicalOwner] = useState("");
  const [securityOwner, setSecurityOwner] = useState("");
  const [assignedOwnerUserId, setAssignedOwnerUserId] = useState("");
  const [sessionError, setSessionError] = useState<string | null>(null);
  const [sessionModalOpen, setSessionModalOpen] = useState(false);
  const [domainsModalOpen, setDomainsModalOpen] = useState(false);
  const [domainModalQuery, setDomainModalQuery] = useState("");
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsListError, setSessionsListError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [recomputeState, setRecomputeState] = useState<"idle" | "running" | "error">("idle");
  const [recomputeError, setRecomputeError] = useState<string | null>(null);
  const [lastRecomputeOkAt, setLastRecomputeOkAt] = useState<string | null>(null);
  const [linkedEvidenceIds, setLinkedEvidenceIds] = useState<string[]>([]);
  const handleQuestionLinkedEvidenceChange = useCallback((ids: string[]) => {
    setLinkedEvidenceIds((prev) => (prev.length === ids.length && prev.every((id, i) => id === ids[i]) ? prev : ids));
  }, []);
  const [newEvidenceIdInput, setNewEvidenceIdInput] = useState("");
  const [naJustification, setNaJustification] = useState("");
  const [govEvidence, setGovEvidence] = useState<Array<{ id: string; title?: string; freshnessStatus?: string; status?: string }>>([]);
  const [govEvidenceLoading, setGovEvidenceLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [remediationMode, setRemediationMode] = useState(false);
  const [remediationControlKey, setRemediationControlKey] = useState<string | null>(null);
  const journeyUrlBootRef = useRef(false);
  const [govEvidenceError, setGovEvidenceError] = useState<string | null>(null);
  const [evidenceSearch, setEvidenceSearch] = useState("");
  const evidenceSearchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedEvidenceSearch, setDebouncedEvidenceSearch] = useState("");
  const [selectedRecommendationId, setSelectedRecommendationId] = useState<string>("");
  const [remediationBusy, setRemediationBusy] = useState(false);
  const [remediationMessage, setRemediationMessage] = useState<string | null>(null);
  const [loadPhase, setLoadPhase] = useState<"idle" | "loading" | "ready" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingRecomputeKeys, setPendingRecomputeKeys] = useState<Set<string>>(() => new Set());
  const resumeAppliedForSessionRef = useRef<string | null>(null);

  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const packIndex = useMemo(() => {
    const m = new Map<string, PackQuestion>();
    for (const d of packs) {
      for (const q of d.questions || []) {
        m.set(`${d.domainKey}::${q.questionKey}`, q);
      }
    }
    return m;
  }, [packs]);

  useEffect(() => {
    const pref = normalizeComplianceStandardCode(preferredStandard || "");
    if (pref && pref !== "ALL" && isJourneyStandardCode(pref)) {
      setJourneyStandard(pref);
      return;
    }
    setJourneyStandard(resolveJourneyStandardFromStorage());
  }, [preferredStandard]);

  useEffect(() => {
    const onEv = () => setJourneyStandard(resolveJourneyStandardFromStorage());
    window.addEventListener("cc:selected-standard", onEv as EventListener);
    window.addEventListener("storage", onEv);
    return () => {
      window.removeEventListener("cc:selected-standard", onEv as EventListener);
      window.removeEventListener("storage", onEv);
    };
  }, []);

  const journeyStandardMountRef = useRef(false);
  useEffect(() => {
    if (!journeyStandardMountRef.current) {
      journeyStandardMountRef.current = true;
      return;
    }
    setSessionId(null);
  }, [journeyStandard]);

  useEffect(() => {
    const parsed = parseRemediationJourneySearch(searchParams.toString());
    if (parsed.standardCode) {
      setJourneyStandard(normalizeComplianceStandardCode(parsed.standardCode));
    }
    if (parsed.sessionId) {
      setSessionId(parsed.sessionId);
    }
    if (parsed.mode === "remediation") {
      setRemediationMode(true);
    }
    if (parsed.controlKey) {
      setRemediationControlKey(parsed.controlKey);
    }
  }, [searchParams]);

  useEffect(() => {
    if (!token || !sessionId || !remediationMode || journeyUrlBootRef.current) return;
    journeyUrlBootRef.current = true;
    void (async () => {
      try {
        await continueRemediationSession(token, sessionId);
        if (!remediationControlKey) {
          const unresolved = await fetchUnresolvedControls(token, sessionId);
          setRemediationControlKey(
            unresolved?.resumeControlKey || unresolved?.controls?.[0]?.controlKey || null,
          );
        }
      } catch {
        /* session may already be in remediation */
      }
    })();
  }, [token, sessionId, remediationMode, remediationControlKey]);

  const loadStaticData = useCallback(async () => {
    setLoadPhase("loading");
    setLoadError(null);
    try {
      const [catRes, packRes, ctlRes, xwRes] = await Promise.all([
        fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_CATALOG, { headers: authHeaders }),
        fetch(API_ENDPOINTS.COMPLIANCE_CURATED_QUESTION_PACKS, { headers: authHeaders }),
        fetch(API_ENDPOINTS.COMPLIANCE_CANONICAL_CONTROLS_CATALOG, { headers: authHeaders }),
        fetch(API_ENDPOINTS.COMPLIANCE_CANONICAL_CONTROLS_CROSSWALK_MATRIX, { headers: authHeaders }).catch(() => null),
      ]);
      const catJson = await catRes.json().catch(() => ({}));
      const packJson = await packRes.json().catch(() => ({}));
      const ctlJson = await ctlRes.json().catch(() => ({}));
      setCatalog(Array.isArray(catJson?.data) ? catJson.data : []);
      setPacks(Array.isArray(packJson?.data) ? packJson.data : []);
      const controls: Record<string, CanonicalControlRow> = {};
      for (const row of Array.isArray(ctlJson?.data) ? ctlJson.data : []) {
        if (row?.code) controls[String(row.code)] = row as CanonicalControlRow;
      }
      setControlsByCode(controls);
      if (xwRes?.ok) {
        const xwJson = await xwRes.json().catch(() => ({}));
        setCrosswalkByControl((xwJson?.data?.byControl as Record<string, any[]>) || {});
      } else {
        setCrosswalkByControl({});
      }
      if (!catRes.ok) throw new Error(catJson?.message || `Catalog HTTP ${catRes.status}`);
      if (!packRes.ok) throw new Error(packJson?.message || `Packs HTTP ${packRes.status}`);
      if (!ctlRes.ok) throw new Error(ctlJson?.message || `Controls HTTP ${ctlRes.status}`);
      setLoadPhase("ready");
    } catch (e: any) {
      setLoadPhase("error");
      setLoadError(e?.message || t("complianceCenterUi.journey.errors.catalog", "Failed to load journey data."));
    }
  }, [authHeaders, t]);

  useEffect(() => {
    void loadStaticData();
  }, [loadStaticData]);

  const loadSessions = useCallback(async () => {
    setSessionsListError(null);
    setSessionsLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSIONS, { headers: authHeaders });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || json?.error || `Sessions ${res.status}`);
      const rows = Array.isArray(json?.data) ? json.data : [];
      setSessionsList(rows);
      return rows;
    } catch (e: any) {
      setSessionsList([]);
      setSessionsListError(e?.message || t("complianceCenterUi.journey.sessionsLoadError", "Could not load sessions."));
      return [];
    } finally {
      setSessionsLoading(false);
    }
  }, [authHeaders, t]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    if (sessionModalOpen) void loadSessions();
  }, [sessionModalOpen, loadSessions]);

  useEffect(() => {
    if (domainsModalOpen) setDomainModalQuery("");
  }, [domainsModalOpen]);

  useEffect(() => {
    if (!sessionModalOpen && !domainsModalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setSessionModalOpen(false);
        setDomainsModalOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [sessionModalOpen, domainsModalOpen]);

  const loadProjections = useCallback(async () => {
    try {
      const url = `${API_ENDPOINTS.COMPLIANCE_CANONICAL_CONTROLS_PROJECTIONS}?standardCode=${encodeURIComponent(journeyStandard)}`;
      const res = await fetch(url, { headers: authHeaders });
      const json = await res.json().catch(() => ({}));
      setProjections(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setProjections([]);
    }
  }, [authHeaders, journeyStandard]);

  const loadApiRecommendations = useCallback(async () => {
    try {
      const url = `${API_ENDPOINTS.COMPLIANCE_RECOMMENDATIONS}?standardCode=${encodeURIComponent(journeyStandard)}`;
      const res = await fetch(url, { headers: authHeaders });
      const json = await res.json().catch(() => ({}));
      setApiRecommendations(Array.isArray(json?.data) ? json.data : []);
    } catch {
      setApiRecommendations([]);
    }
  }, [authHeaders, journeyStandard]);

  const refreshSession = useCallback(
    async (id: string) => {
      setSessionError(null);
      try {
        const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSION(id), { headers: authHeaders });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.message || `Session ${res.status}`);
        setSession(json.data || null);
      } catch (e: any) {
        setSession(null);
        setSessionError(e?.message || t("complianceCenterUi.journey.errors.session", "Failed to load session."));
      }
    },
    [authHeaders, t],
  );

  useEffect(() => {
    if (sessionId) {
      void refreshSession(sessionId);
    } else {
      setSession(null);
    }
  }, [sessionId, refreshSession]);

  useEffect(() => {
    void loadProjections();
  }, [loadProjections]);

  useEffect(() => {
    void loadApiRecommendations();
  }, [loadApiRecommendations]);

  const journeyDomains = useMemo(() => {
    const sorted = [...catalog].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const key = (d: CatalogDomain) => String(d.key);
    if (journeyStandard === "GDPR") return sorted.filter((d) => key(d).startsWith("GDPR_"));
    if (journeyStandard === "ISO27001") return sorted.filter((d) => key(d).startsWith("ISO27001_"));
    if (journeyStandard === "ISO42001") return sorted.filter((d) => key(d).startsWith("ISO42001_"));
    if (journeyStandard === "NIS2") return sorted.filter((d) => key(d).startsWith("NIS2_"));
    if (journeyStandard === "SOC2") return sorted.filter((d) => key(d).startsWith("SOC2_"));
    return sorted.filter(
      (d) =>
        !key(d).startsWith("GDPR_") &&
        !key(d).startsWith("ISO27001_") &&
        !key(d).startsWith("ISO42001_") &&
        !key(d).startsWith("NIS2_") &&
        !key(d).startsWith("SOC2_"),
    );
  }, [catalog, journeyStandard]);

  const flatJourneySteps = useMemo(() => {
    const out: { domainKey: string; questionKey: string }[] = [];
    for (const d of journeyDomains) {
      for (const q of d.questions || []) out.push({ domainKey: d.key, questionKey: q.questionKey });
    }
    return out;
  }, [journeyDomains]);

  const goJourneyFlatStep = useCallback(
    (idx: number) => {
      const step = flatJourneySteps[idx];
      if (!step) return;
      setSelectedDomainKey(String(step.domainKey));
      setSelectedQuestionKey(step.questionKey);
    },
    [flatJourneySteps],
  );

  useEffect(() => {
    resumeAppliedForSessionRef.current = null;
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId || !selectedDomainKey || !selectedQuestionKey) return;
    writeJourneyResumePointer(
      journeyResumeStorageKey(sessionId, journeyStandard, "journey-flat"),
      { domainKey: String(selectedDomainKey), questionKey: selectedQuestionKey },
    );
  }, [sessionId, selectedDomainKey, selectedQuestionKey, journeyStandard]);

  const sequentialStepIndex = useMemo(() => {
    if (navMode !== "SEQUENTIAL") return -1;
    const i = flatJourneySteps.findIndex((s) => s.domainKey === selectedDomainKey && s.questionKey === selectedQuestionKey);
    return i >= 0 ? i : 0;
  }, [navMode, flatJourneySteps, selectedDomainKey, selectedQuestionKey]);

  const filteredSessionsForStandard = useMemo(
    () => sessionsList.filter((s) => standardsEquivalent(s.standardCode, journeyStandard)),
    [journeyStandard, sessionsList],
  );

  const domainsModalFiltered = useMemo(() => {
    const q = domainModalQuery.trim().toLowerCase();
    if (!q) return journeyDomains;
    return journeyDomains.filter((d) => pickLoc(d.localization, lang).toLowerCase().includes(q));
  }, [domainModalQuery, journeyDomains, lang]);

  const domainStateMap = useMemo(() => {
    const m = new Map<string, any>();
    for (const row of session?.domainStates || []) {
      m.set(String(row.domainKey), row);
    }
    return m;
  }, [session]);

  const selectedDomain = useMemo(
    () => journeyDomains.find((d) => String(d.key) === String(selectedDomainKey)) || null,
    [journeyDomains, selectedDomainKey],
  );

  const selectedQuestion = useMemo(
    () => selectedDomain?.questions.find((q) => q.questionKey === selectedQuestionKey) || null,
    [selectedDomain, selectedQuestionKey],
  );

  const selectedPackQ = useMemo(() => {
    if (!selectedDomain || !selectedQuestion) return undefined;
    return packIndex.get(`${selectedDomain.key}::${selectedQuestion.questionKey}`);
  }, [packIndex, selectedDomain, selectedQuestion]);

  const projectionForSelected = useMemo(() => {
    const code = selectedQuestion?.canonicalControlCode;
    if (!code) return null;
    return projections.find((p) => p.control?.code === code) || null;
  }, [projections, selectedQuestion]);

  const evidenceWarnings = useMemo(
    () =>
      journeyEvidenceWarnings({
        answerChoice,
        linkedEvidenceCount: linkedEvidenceIds.length,
        naJustification,
      }),
    [answerChoice, linkedEvidenceIds.length, naJustification],
  );

  const evidenceBucket = useMemo(
    () =>
      deriveEvidenceBucket({
        answerChoice,
        linkedEvidenceCount: linkedEvidenceIds.length,
        projectionCompleteness: projectionForSelected?.evidenceCompleteness,
      }),
    [answerChoice, linkedEvidenceIds.length, projectionForSelected?.evidenceCompleteness],
  );

  const apiRecsForControl = useMemo(() => {
    const code = selectedQuestion?.canonicalControlCode;
    if (!code) return apiRecommendations;
    return apiRecommendations.filter(
      (r: any) => !r.controlCode || String(r.controlCode) === code || String(r.controlKey || "") === code,
    );
  }, [apiRecommendations, selectedQuestion?.canonicalControlCode]);

  const workbenchStats = useMemo(
    () =>
      computeJourneyWorkbenchStats({
        domains: journeyDomains.map((d) => ({
          key: d.key,
          questions: (d.questions || []).map((q) => ({
            questionKey: q.questionKey,
            canonicalControlCode: q.canonicalControlCode,
          })),
        })),
        responses: (session?.responses || []) as Parameters<typeof computeJourneyWorkbenchStats>[0]["responses"],
        projections: projections.map((p) => ({
          status: p.status,
          control: p.control ? { code: p.control.code } : undefined,
        })),
      }),
    [journeyDomains, session?.responses, projections],
  );

  const domainQuestionStats = useMemo(() => {
    if (!selectedDomain || !session) return { answered: 0, total: 0, missingKeys: [] as string[] };
    const qs = selectedDomain.questions || [];
    const missingKeys: string[] = [];
    let answered = 0;
    for (const q of qs) {
      const r = session.responses?.find(
        (x: any) => x.questionKey === q.questionKey && String(x.domainKey) === String(selectedDomain.key),
      );
      if (r?.answerValue != null && String(r.answerValue).trim().length > 0) answered += 1;
      else missingKeys.push(q.questionKey);
    }
    return { answered, total: qs.length, missingKeys };
  }, [selectedDomain, session]);

  const standardAllowed = useMemo(() => {
    if (!enabledStandards.length) return true;
    return enabledStandards.some((code) => standardsEquivalent(code, journeyStandard));
  }, [enabledStandards, journeyStandard]);

  const controlCentricUxActive = useMemo(
    () => isControlCentricUxEnabledForStandard(journeyStandard, standardAllowed),
    [journeyStandard, standardAllowed],
  );

  useEffect(() => {
    if (!controlCentricUxActive || !session?.navigationMode) return;
    const mode = String(session.navigationMode).toUpperCase();
    if (mode === "SEQUENTIAL" || mode === "FREE") {
      setNavMode(mode);
    }
  }, [controlCentricUxActive, session?.navigationMode, setNavMode]);

  const controlCentricCompletionView = useMemo(() => {
    if (!controlCentricUxActive || !sessionId) return false;
    return (
      controlCentricSessionCompleted ||
      String(controlCentricOverview?.session?.status || "").toUpperCase() === "COMPLETED"
    );
  }, [
    controlCentricUxActive,
    sessionId,
    controlCentricSessionCompleted,
    controlCentricOverview?.session?.status,
  ]);

  const handleControlCentricCompletedChange = useCallback((completed: boolean) => {
    setControlCentricSessionCompleted(completed);
    if (completed) setSessionModalOpen(false);
  }, []);

  const openSessionSelector = useCallback(() => {
    setSessionModalOpen(true);
    void loadSessions();
  }, [loadSessions]);

  useEffect(() => {
    setControlCentricSessionCompleted(false);
  }, [sessionId]);

  const recommendationRiskSummary = useMemo(() => {
    const riskyCodes = new Set(
      projections
        .filter((p) => {
          const s = String(p.status || "").toUpperCase();
          return s === "NON_COMPLIANT" || s === "PARTIAL" || s === "PENDING_EVIDENCE";
        })
        .map((p) => String(p.control?.code || ""))
        .filter(Boolean),
    );
    const riskyQuestionKeys = new Set<string>();
    for (const d of journeyDomains) {
      for (const q of d.questions || []) {
        const p = projections.find((x) => String(x.control?.code) === String(q.canonicalControlCode));
        const s = String(p?.status || "").toUpperCase();
        if (s === "NON_COMPLIANT" || s === "PARTIAL" || s === "PENDING_EVIDENCE") riskyQuestionKeys.add(q.questionKey);
      }
    }
    return matchRecommendationsAgainstRiskSet({
      riskyControlCodes: riskyCodes,
      riskyQuestionKeys,
      recommendations: apiRecommendations,
    });
  }, [journeyDomains, projections, apiRecommendations]);

  const workspaceSummary = useMemo((): OperationalJourneyWorkspaceSummary | null => {
    if (loadPhase !== "ready") return null;
    const cp = controlCentricUxActive ? controlCentricOverview?.progress : null;
    return {
      journeyStandard,
      sessionId,
      totalQ: cp ? cp.controlsTotal : workbenchStats.totalQ,
      answered: cp ? cp.controlsCompleted : workbenchStats.answered,
      unanswered: cp ? Math.max(0, cp.controlsTotal - cp.controlsCompleted) : workbenchStats.unanswered,
      evidenceMissingAnswers: cp ? cp.controlsWithGaps : workbenchStats.evidenceMissingAnswers,
      evidenceLinkedAnswers: cp ? cp.controlsEvidenceSupported : workbenchStats.evidenceLinkedAnswers,
      compliantProjections: cp ? cp.controlsEvidenceSupported : workbenchStats.compliant,
      partialProjections: cp ? cp.partialControls : workbenchStats.partial,
      nonCompliantProjections: cp ? cp.nonCompliantControls : workbenchStats.nonCompliant,
      pendingEvidenceProjections: cp ? 0 : workbenchStats.pendingEvidence,
      domainAnswered: domainQuestionStats.answered,
      domainTotal: domainQuestionStats.total,
      domainMissingCount: domainQuestionStats.missingKeys.length,
      recommendationsForRiskControls: recommendationRiskSummary.displayCount,
      recommendationMatchMode: recommendationRiskSummary.mode,
      recommendationsApiTotal: recommendationRiskSummary.totalRecommendations,
    };
  }, [
    loadPhase,
    journeyStandard,
    sessionId,
    controlCentricUxActive,
    controlCentricOverview?.progress,
    workbenchStats,
    domainQuestionStats,
    recommendationRiskSummary.displayCount,
    recommendationRiskSummary.mode,
    recommendationRiskSummary.totalRecommendations,
  ]);

  useEffect(() => {
    onWorkspaceSummaryChange?.(workspaceSummary);
  }, [onWorkspaceSummaryChange, workspaceSummary]);

  const statusExplainLines = useMemo(() => {
    if (journeyStandard === "SOC2" || journeyStandard === "NIS2" || journeyStandard === "GDPR") return [];
    const lines: string[] = [];
    const code = selectedQuestion?.canonicalControlCode;
    const r = session?.responses?.find((x: any) => x.questionKey === selectedQuestionKey);
    const st = String(projectionForSelected?.status || "").toUpperCase();
    const ec = projectionForSelected?.evidenceCompleteness;
    if (!code) return lines;
    lines.push(`control:${code}`);
    if (journeyStandard === "GDPR" && selectedQuestion?.articleRefs?.length) {
      lines.push(`gdprArticles:${selectedQuestion.articleRefs.join(", ")}`);
    }
    if ((journeyStandard === "GDPR" || journeyStandard === "ISO27001" || journeyStandard === "ISO42001") && selectedQuestion?.whyThisMatters) {
      lines.push(`whyThisMatters:${pickLoc(selectedQuestion.whyThisMatters, lang)}`);
    }
    if ((journeyStandard === "GDPR" || journeyStandard === "ISO27001" || journeyStandard === "ISO42001") && selectedQuestion?.deterministicRulesLocalized) {
      lines.push(`deterministicRules:${pickLoc(selectedQuestion.deterministicRulesLocalized, lang)}`);
    }
    if (journeyStandard === "GDPR" && selectedQuestion?.inheritanceMappingHints) {
      lines.push(`inheritanceHints:${pickLoc(selectedQuestion.inheritanceMappingHints, lang)}`);
    }
    if (r?.answerValue) lines.push(`answer:${String(r.answerValue)}`);
    lines.push(`linkedEvidenceCount:${linkedEvidenceIds.length}`);
    if (ec != null) lines.push(`projectionEvidenceCompleteness:${Math.round(Number(ec))}`);
    if (st) lines.push(`projectionStatus:${st}`);
    if (st === "PENDING_EVIDENCE" || st === "PARTIAL") lines.push("hint:evidence_or_depth");
    if (st === "NON_COMPLIANT") lines.push("hint:remediation");
    if (evidenceWarnings.yesPartialNoEvidence) lines.push("hint:noEvidenceForPositiveAnswer");
    return lines;
  }, [
    journeyStandard,
    lang,
    selectedQuestion?.articleRefs,
    selectedQuestion?.whyThisMatters,
    selectedQuestion?.deterministicRulesLocalized,
    selectedQuestion?.inheritanceMappingHints,
    selectedQuestion?.canonicalControlCode,
    session,
    selectedQuestionKey,
    projectionForSelected,
    linkedEvidenceIds.length,
    evidenceWarnings.yesPartialNoEvidence,
  ]);

  const curatedRecommendationText = useMemo(() => {
    const fromPack =
      stringifyLocLeaf(selectedPackQ?.recommendationLocalization?.[lang]?.text) ||
      stringifyLocLeaf(selectedPackQ?.recommendationLocalization?.en?.text);
    return fromPack || pickLoc(selectedQuestion?.recommendationHint, lang) || "";
  }, [selectedPackQ, lang, selectedQuestion?.recommendationHint]);

  const selectedPackDomain = useMemo(
    () => packs.find((p) => p.domainKey === selectedDomainKey),
    [packs, selectedDomainKey],
  );

  const domainCuratedScenarios = useMemo(() => {
    const rows = (selectedPackDomain as PackDomain | undefined)?.curatedScenarioRecommendations;
    return Array.isArray(rows) ? rows : [];
  }, [selectedPackDomain]);

  const inheritanceRows = useMemo(() => {
    if (journeyStandard !== "GDPR" || !selectedQuestion?.canonicalControlCode) return [];
    const meta = controlsByCode[selectedQuestion.canonicalControlCode]?.metadata;
    const maps = Array.isArray(meta?.inheritedMappings) ? meta.inheritedMappings : [];
    return maps;
  }, [journeyStandard, selectedQuestion?.canonicalControlCode, controlsByCode]);

  const inferredActions = useMemo(
    () =>
      inferActionTypes({
        answerChoice,
        hasEvidence: linkedEvidenceIds.length > 0,
        projectionStatus: projectionForSelected?.status,
        recommendationText: curatedRecommendationText,
      }),
    [answerChoice, linkedEvidenceIds.length, projectionForSelected?.status, curatedRecommendationText],
  );

  const soc2EvidenceStale = useMemo(() => {
    if (journeyStandard !== "SOC2") return false;
    if (evidenceBucket === "stale_unknown") return true;
    const staleGov = govEvidence.some((row) => {
      const fs = String(row.freshnessStatus || row.status || "").toLowerCase();
      return fs.includes("stale") || fs.includes("expired");
    });
    return staleGov;
  }, [journeyStandard, evidenceBucket, govEvidence]);

  const soc2LastOperationalReviewLabel = useMemo(() => {
    if (journeyStandard !== "SOC2") return "";
    return formatIsoDate(session?.updatedAt || lastRecomputeOkAt);
  }, [journeyStandard, session?.updatedAt, lastRecomputeOkAt]);

  const selectedQuestionPresentation = useMemo(() => {
    if (!selectedQuestion || !selectedDomain) return null;
    const row = session?.responses?.find(
      (x: any) => x.questionKey === selectedQuestionKey && String(x.domainKey) === String(selectedDomain.key),
    );
    const pl =
      row?.answerPayload && typeof row.answerPayload === "object"
        ? (row.answerPayload as Record<string, string>)
        : {};
    const projRow = projections.find((p) => p.control?.code === selectedQuestion.canonicalControlCode);
    const pendK = `${selectedDomain.key}::${selectedQuestion.questionKey}`;
    return resolveJourneyQuestionRowPresentation({
      answerValue: row?.answerValue ?? (answerChoice ? mapAnswerToApi(answerChoice) : undefined),
      evidenceIds: row?.evidenceIds ?? linkedEvidenceIds,
      linkedEvidence: row?.linkedEvidence,
      evidenceSufficiencyState: row?.evidenceSufficiency?.state,
      naJustification: String(pl.naJustification || pl.notApplicableRationale || naJustification || ""),
      projectionStatus: projRow?.status,
      evidenceCompleteness: projRow?.evidenceCompleteness,
      pendingRecompute: Boolean(sessionId && pendingRecomputeKeys.has(pendK)),
      standardCode: journeyStandard,
      articleRefs: selectedQuestion.articleRefs,
      evidenceModel: selectedQuestion.evidenceModel as Record<string, unknown> | undefined,
    });
  }, [
    selectedQuestion,
    selectedDomain,
    session?.responses,
    selectedQuestionKey,
    answerChoice,
    linkedEvidenceIds,
    naJustification,
    projections,
    sessionId,
    pendingRecomputeKeys,
    journeyStandard,
  ]);

  useEffect(() => {
    if (!selectedDomainKey && journeyDomains.length) {
      setSelectedDomainKey(String(journeyDomains[0].key));
    }
  }, [journeyDomains, selectedDomainKey]);

  useEffect(() => {
    if (!journeyDomains.length) return;
    if (selectedDomainKey == null) return;
    const still = journeyDomains.some((d) => String(d.key) === String(selectedDomainKey));
    if (!still) {
      setSelectedDomainKey(String(journeyDomains[0].key));
      setSelectedQuestionKey(journeyDomains[0].questions[0]?.questionKey || null);
    }
  }, [journeyStandard, journeyDomains, selectedDomainKey]);

  useEffect(() => {
    if (!selectedDomain) return;
    if (navMode !== "SEQUENTIAL") return;
    const first = selectedDomain.questions[0];
    if (first && !selectedQuestionKey) setSelectedQuestionKey(first.questionKey);
  }, [selectedDomain, selectedQuestionKey, navMode]);

  useEffect(() => {
    const r = session?.responses?.find((x: any) => x.questionKey === selectedQuestionKey);
    if (!r) {
      setAnswerChoice("");
      setBusinessOwner("");
      setTechnicalOwner("");
      setSecurityOwner("");
      setAssignedOwnerUserId("");
      setLinkedEvidenceIds([]);
      setNaJustification("");
      return;
    }
    const av = String(r.answerValue || "").toLowerCase();
    if (av === "yes" || av === "true" || av === "1") setAnswerChoice("YES");
    else if (av === "partial") setAnswerChoice("PARTIAL");
    else if (av === "n/a" || av === "na" || av === "not_applicable") setAnswerChoice("NOT_APPLICABLE");
    else if (av === "no" || av === "false" || av === "0") setAnswerChoice("NO");
    else setAnswerChoice(av ? av.toUpperCase() : "");
    const pl = (r.answerPayload && typeof r.answerPayload === "object" ? r.answerPayload : {}) as Record<string, string>;
    setBusinessOwner(String(pl.businessOwnerLabel || ""));
    setTechnicalOwner(String(pl.technicalOwnerLabel || ""));
    setSecurityOwner(String(pl.securityOwnerLabel || ""));
    setAssignedOwnerUserId(r.assignedOwnerUserId ? String(r.assignedOwnerUserId) : "");
    setNaJustification(String(pl.naJustification || pl.notApplicableRationale || ""));
    if (featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 && Array.isArray(r.linkedEvidence) && r.linkedEvidence.length) {
      setLinkedEvidenceIds(r.linkedEvidence.map((x: { evidenceId?: string }) => String(x.evidenceId)).filter(Boolean));
      return;
    }
    const ev = r.evidenceIds;
    if (Array.isArray(ev)) setLinkedEvidenceIds(ev.map((x: any) => String(x)).filter(Boolean));
    else if (typeof ev === "string") setLinkedEvidenceIds(ev.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean));
    else setLinkedEvidenceIds([]);
  }, [session, selectedQuestionKey]);

  useEffect(() => {
    if (evidenceSearchDebounceRef.current) clearTimeout(evidenceSearchDebounceRef.current);
    evidenceSearchDebounceRef.current = setTimeout(() => setDebouncedEvidenceSearch(evidenceSearch.trim()), 400);
    return () => {
      if (evidenceSearchDebounceRef.current) clearTimeout(evidenceSearchDebounceRef.current);
    };
  }, [evidenceSearch]);

  useEffect(() => {
    setRemediationMessage(null);
    setSelectedRecommendationId("");
  }, [selectedQuestionKey]);

  useEffect(() => {
    if (!token || !selectedQuestion?.canonicalControlCode) {
      setGovEvidence([]);
      return;
    }
    let cancelled = false;
    const run = async () => {
      setGovEvidenceLoading(true);
      setGovEvidenceError(null);
      try {
        const params = new URLSearchParams();
        params.set("controlCode", String(selectedQuestion.canonicalControlCode));
        if (debouncedEvidenceSearch) params.set("search", debouncedEvidenceSearch);
        const url = `${API_ENDPOINTS.GOVERNANCE_EVIDENCE}?${params.toString()}`;
        const res = await fetch(url, { headers: authHeaders });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.message || json?.error || `Evidence list ${res.status}`);
        const rows = Array.isArray(json?.evidence) ? json.evidence : Array.isArray(json?.data) ? json.data : [];
        if (!cancelled)
          setGovEvidence(
            rows.slice(0, 25).map((x: any) => ({
              id: String(x.id),
              title: stringifyLocLeaf(x.title),
              freshnessStatus: x.freshnessStatus,
              status: x.status,
            })),
          );
      } catch (e: any) {
        if (!cancelled) {
          setGovEvidence([]);
          setGovEvidenceError(e?.message || t("complianceCenterUi.journey.governanceEvidenceError", "Governance evidence unavailable."));
        }
      } finally {
        if (!cancelled) setGovEvidenceLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, selectedQuestion?.canonicalControlCode, debouncedEvidenceSearch, authHeaders]);

  const createSession = async () => {
    setSessionError(null);
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSIONS, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          navigationMode: navMode,
          standardCode: journeyStandard,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Create session failed");
      const id = json?.data?.id;
      if (!id) throw new Error("No session id returned");
      setSessionId(String(id));
      await loadSessions();
      await refreshSession(String(id));
      await loadProjections();
      await loadApiRecommendations();
      setSessionModalOpen(false);
    } catch (e: any) {
      setSessionError(e?.message || t("complianceCenterUi.journey.errors.session", "Failed to create session."));
    }
  };

  const saveAnswer = async () => {
    if (!sessionId || !selectedDomain || !selectedQuestion) return;
    if (!answerChoice) {
      setSaveError(t("complianceCenterUi.journey.errors.noAnswer", "Select an answer before saving."));
      setSaveState("error");
      return;
    }
    setSaveState("saving");
    setSaveError(null);
    const pendingKey = `${selectedDomain.key}::${selectedQuestion.questionKey}`;
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSION_ANSWERS(sessionId), {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({
          domainKey: selectedDomain.key,
          questionKey: selectedQuestion.questionKey,
          canonicalControlCode: selectedQuestion.canonicalControlCode,
          answerValue: mapAnswerToApi(answerChoice),
          answerPayload: {
            businessOwnerLabel: businessOwner || undefined,
            technicalOwnerLabel: technicalOwner || undefined,
            securityOwnerLabel: securityOwner || undefined,
            naJustification: naJustification.trim() || undefined,
            notApplicableRationale: naJustification.trim() || undefined,
          },
          assignedOwnerUserId: assignedOwnerUserId.trim() || null,
          evidenceIds: linkedEvidenceIds.length ? linkedEvidenceIds : null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Save failed");
      setPendingRecomputeKeys((prev) => new Set(prev).add(pendingKey));
      setSaveState("saved");
      try {
        await refreshSession(sessionId);
        await loadProjections();
        await loadApiRecommendations();
      } finally {
        setPendingRecomputeKeys((prev) => {
          const next = new Set(prev);
          next.delete(pendingKey);
          return next;
        });
      }
      setTimeout(() => setSaveState("idle"), 2000);
    } catch (e: any) {
      setPendingRecomputeKeys((prev) => {
        const next = new Set(prev);
        next.delete(pendingKey);
        return next;
      });
      setSaveState("error");
      setSaveError(e?.message || t("complianceCenterUi.journey.errors.save", "Failed to save answer."));
    }
  };

  const saveControlCentricQuestion = useCallback(
    async (params: {
      domainKey: string;
      questionKey: string;
      canonicalControlCode: string | null;
      answerValue: string;
      assessorNote: string;
    }) => {
      if (!sessionId) return;
      const { domainKey, questionKey, canonicalControlCode, answerValue, assessorNote } = params;
      const pendingKey = `${domainKey}::${questionKey}`;
      setSaveState("saving");
      setSaveError(null);
      try {
        const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSION_ANSWERS(sessionId), {
          method: "POST",
          headers: { ...authHeaders, "Content-Type": "application/json" },
          body: JSON.stringify({
            domainKey,
            questionKey,
            canonicalControlCode,
            answerValue: mapAnswerToApi(answerValue),
            answerPayload: {
              assessorNote: assessorNote.trim() || undefined,
              naJustification: assessorNote.trim() || undefined,
            },
          }),
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json?.message || "Save failed");
        setPendingRecomputeKeys((prev) => new Set(prev).add(pendingKey));
        setSaveState("saved");
        try {
          await refreshSession(sessionId);
          await loadProjections();
          await loadApiRecommendations();
        } finally {
          setPendingRecomputeKeys((prev) => {
            const next = new Set(prev);
            next.delete(pendingKey);
            return next;
          });
        }
        setTimeout(() => setSaveState("idle"), 2000);
      } catch (e: any) {
        setPendingRecomputeKeys((prev) => {
          const next = new Set(prev);
          next.delete(pendingKey);
          return next;
        });
        setSaveState("error");
        setSaveError(e?.message || t("complianceCenterUi.journey.errors.save", "Failed to save answer."));
        throw e;
      }
    },
    [sessionId, authHeaders, refreshSession, loadProjections, loadApiRecommendations, t],
  );

  const runRecompute = useCallback(async () => {
    if (!sessionId) return;
    setRecomputeState("running");
    setRecomputeError(null);
    try {
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_HUMAN_WORKFLOW_SESSION_RECOMPUTE(sessionId), {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Recompute failed");
      await refreshSession(sessionId);
      await loadProjections();
      await loadApiRecommendations();
      setRecomputeState("idle");
      setLastRecomputeOkAt(new Date().toISOString());
    } catch (e: any) {
      setRecomputeState("error");
      setRecomputeError(e?.message || t("complianceCenterUi.journey.errors.recompute", "Recompute failed."));
    }
  }, [sessionId, authHeaders, refreshSession, loadProjections, loadApiRecommendations, t]);

  useEffect(() => {
    const onJourneyCommand = (ev: Event) => {
      const detail = (ev as CustomEvent<{ type?: string; elementId?: string }>).detail;
      if (!detail?.type) return;
      if (detail.type === "scroll_element" && detail.elementId) {
        document.getElementById(detail.elementId)?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (detail.type === "open_session_setup") {
        setSessionModalOpen(true);
        return;
      }
      if (detail.type === "open_domains_modal") {
        setDomainsModalOpen(true);
        return;
      }
      if (detail.type === "run_recompute") {
        void runRecompute();
        return;
      }
      if (detail.type === "focus_questions_panel") {
        document.getElementById("cc-journey-questions-panel")?.scrollIntoView({ behavior: "smooth", block: "start" });
        if (sessionId && selectedDomain && domainQuestionStats.missingKeys.length) {
          const mk = domainQuestionStats.missingKeys[0];
          if (mk) setSelectedQuestionKey(mk);
        }
      }
    };
    window.addEventListener("cc:journey-command", onJourneyCommand as EventListener);
    return () => window.removeEventListener("cc:journey-command", onJourneyCommand as EventListener);
  }, [runRecompute, sessionId, selectedDomain, domainQuestionStats.missingKeys]);

  const createRemediationFromJourney = async () => {
    setRemediationMessage(null);
    if (!featureFlags.REMEDIATION_WORKFLOW_V2) {
      setRemediationMessage(
        t("complianceCenterUi.journey.remediationDisabledFlag", "Remediation task creation requires REMEDIATION_WORKFLOW_V2."),
      );
      return;
    }
    if (!selectedRecommendationId) {
      setRemediationMessage(
        t("complianceCenterUi.journey.remediationPickRec", "Select a persisted recommendation to attach the task."),
      );
      return;
    }
    setRemediationBusy(true);
    try {
      const title =
        selectedQuestion?.questionKey && selectedQuestion.canonicalControlCode
          ? `Journey: ${selectedQuestion.canonicalControlCode} · ${selectedQuestion.questionKey}`
          : t("complianceCenterUi.journey.remediationDefaultTitle", "Operational journey remediation");
      const body = {
        recommendationId: selectedRecommendationId,
        title,
        description: curatedRecommendationText || pickLoc(selectedQuestion?.remediationHint, lang) || undefined,
        priority: "P2",
        status: "OPEN",
        progressPercent: 0,
        metadata: {
          source: "OPERATIONAL_JOURNEY",
          humanWorkflowSessionId: sessionId || null,
          domainKey: selectedDomain?.key || null,
          questionKey: selectedQuestion?.questionKey || null,
          standardCode: journeyStandard,
        },
      };
      const res = await fetch(API_ENDPOINTS.COMPLIANCE_REMEDIATION_TASKS, {
        method: "POST",
        headers: { ...authHeaders, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json?.message || "Remediation task create failed");
      setRemediationMessage(t("complianceCenterUi.journey.remediationCreated", "Remediation task created."));
    } catch (e: any) {
      setRemediationMessage(e?.message || t("complianceCenterUi.journey.remediationFailed", "Could not create remediation task."));
    } finally {
      setRemediationBusy(false);
    }
  };

  const soc2EvidenceDomainKey = useMemo(() => {
    if (!controlCentricUxActive) return selectedDomain?.key || "";
    const withQuestions = journeyDomains.find((d) => (d.questions?.length || 0) > 0);
    return withQuestions?.key || selectedDomain?.key || "SOC2_SECURITY";
  }, [controlCentricUxActive, journeyDomains, selectedDomain?.key]);

  useEffect(() => {
    if (controlCentricUxActive) return;
    if (navMode !== "SEQUENTIAL") return;
    if (!sessionId || !session || !flatJourneySteps.length) return;
    if (resumeAppliedForSessionRef.current === sessionId) return;

    const resumeKey = journeyResumeStorageKey(sessionId, journeyStandard, "journey-flat");
    const saved = readJourneyResumePointer(resumeKey);
    const idx = resolveFlatJourneyResumeIndex(flatJourneySteps, session.responses, saved);
    goJourneyFlatStep(idx);
    resumeAppliedForSessionRef.current = sessionId;
  }, [
    controlCentricUxActive,
    navMode,
    sessionId,
    session,
    flatJourneySteps,
    journeyStandard,
    goJourneyFlatStep,
  ]);

  const standardLabel = (code: string) => getStandardDisplayName(t, code);

  const domainBrowseSubtitle = useMemo(() => {
    const domName = selectedDomain
      ? pickLoc(selectedDomain.localization, lang)
      : t("complianceCenterUi.journey.chooseDomain", "Choose a domain");
    const st = selectedDomain ? domainStateMap.get(selectedDomain.key) : null;
    const pct = st?.progressPercent != null && Number.isFinite(Number(st.progressPercent)) ? `${Math.round(Number(st.progressPercent))}%` : "";
    if (pct) return `${domName} · ${t("complianceCenterUi.journey.progress", "Progress")}: ${pct}`;
    return domName;
  }, [domainStateMap, lang, selectedDomain, t]);

  const sessionCardSubtitle = useMemo(() => {
    if (sessionId) {
      return t("complianceCenterUi.journey.sessionCardSubtitleActive", "Active session {{id}}", {
        id: String(sessionId).slice(0, 8),
      });
    }
    return t("complianceCenterUi.journey.sessionCardSubtitleIdle", "No active session");
  }, [sessionId, t]);

  const renderGdprEnterpriseQuestionWorkbench = (opts?: { embedded?: boolean }) => {
    if (!selectedQuestion) return null;
    const wrap = opts?.embedded
      ? "mt-3 space-y-5 border-t border-gray-100 pt-3"
      : "mt-4 space-y-5 border-t border-gray-100 pt-4";
    const em = selectedQuestion.evidenceModel as Record<string, LocTriple> | undefined;
    const assessmentTone = deriveGdprAssessmentTone({
      presentationAccent: selectedQuestionPresentation?.accent,
      projectionStatus: projectionForSelected?.status,
    });
    const evidenceTone: GdprEnterpriseTone =
      evidenceBucket === "missing"
        ? "red"
        : evidenceBucket === "weak" || evidenceBucket === "stale_unknown"
          ? "amber"
          : evidenceBucket === "not_required"
            ? "green"
            : "green";
    const operationalLabel =
      assessmentTone === "green"
        ? t(`${GDPR_GE}.operationalGreen`, "Operationally maintained")
        : assessmentTone === "red"
          ? t(`${GDPR_GE}.operationalRed`, "Operational gaps identified")
          : t(`${GDPR_GE}.operationalAmber`, "Requires additional validation");
    const evidenceLabel =
      evidenceTone === "green"
        ? t(`${GDPR_GE}.evidenceComplete`, "Linked records in place")
        : evidenceTone === "red"
          ? t(`${GDPR_GE}.evidenceMissing`, "Records still needed")
          : evidenceTone === "amber"
            ? t(`${GDPR_GE}.evidenceWeak`, "Records may need strengthening")
            : t(`${GDPR_GE}.evidenceNotRequired`, "Not required for this answer");
    const uploadBullets = [
      ...parseSemicolonBulletList(pickLoc(em?.suggestedArtifacts, lang)),
      ...parseSemicolonBulletList(pickLoc(em?.expectedEvidence, lang) || pickLoc(selectedQuestion.evidenceHint, lang)),
    ];
    const strongBullets = parseSemicolonBulletList(pickLoc(em?.strongEvidenceIndicators, lang));
    const weakBullets = parseSemicolonBulletList(pickLoc(em?.weakEvidenceIndicators, lang));
    const optionalBullets = parseSemicolonBulletList(pickLoc(em?.optionalEvidence, lang));
    const commonProblems = GDPR_ENTERPRISE_COMMON_PROBLEMS[lang];
    const reviewCadenceLabel =
      selectedQuestion.reviewCadenceLocalized
        ? pickLoc(selectedQuestion.reviewCadenceLocalized, lang)
        : selectedQuestion.reviewCadence || "";
    const lastUpdateLabel = formatIsoDate(session?.updatedAt || lastRecomputeOkAt);

    return (
      <div className={wrap}>
        <section className={ccStyles.gdprExecutiveStrip}>
          <div>
            <div className={ccStyles.gdprExecutiveItemLabel}>{t(`${GDPR_GE}.executiveOverall`, "Overall operational state")}</div>
            <div className={`${ccStyles.gdprExecutiveItemValue} ${gdprToneExecutiveClass(assessmentTone)}`}>{operationalLabel}</div>
          </div>
          <div>
            <div className={ccStyles.gdprExecutiveItemLabel}>{t(`${GDPR_GE}.executiveEvidence`, "Evidence completeness")}</div>
            <div className={`${ccStyles.gdprExecutiveItemValue} ${gdprToneExecutiveClass(evidenceTone)}`}>{evidenceLabel}</div>
          </div>
          <div>
            <div className={ccStyles.gdprExecutiveItemLabel}>{t(`${GDPR_GE}.executiveActions`, "Outstanding actions")}</div>
            <div className={ccStyles.gdprExecutiveItemValue}>
              {inferredActions.length > 0
                ? t(`${GDPR_GE}.actionsCount`, "{{count}} suggested follow-ups", { count: inferredActions.length })
                : t(`${GDPR_GE}.actionsNone`, "None identified for this question")}
            </div>
          </div>
          <div>
            <div className={ccStyles.gdprExecutiveItemLabel}>{t(`${GDPR_GE}.executiveLastUpdate`, "Last review / update")}</div>
            <div className={ccStyles.gdprExecutiveItemValue}>{lastUpdateLabel || "—"}</div>
          </div>
        </section>

        <header className={ccStyles.gdprQuestionHeader}>
          <h3 className={ccStyles.gdprQuestionTitle}>{pickLoc(selectedQuestion.localization, lang)}</h3>
          {selectedQuestion.whyThisMatters ? (
            <p className={ccStyles.gdprQuestionWhy}>{pickLoc(selectedQuestion.whyThisMatters, lang)}</p>
          ) : null}
          <div className={ccStyles.gdprMetaRow}>
            {reviewCadenceLabel ? (
              <span className={ccStyles.gdprMetaChip}>
                {t(`${GDPR_GE}.reviewCadenceShort`, "Review cadence")}: {reviewCadenceLabel}
              </span>
            ) : null}
            {selectedQuestion.ownerRoleKey ? (
              <span className={ccStyles.gdprMetaChip}>
                {t(`${GDPR_GE}.ownerRole`, "Owner role")}: {selectedQuestion.ownerRoleKey}
              </span>
            ) : null}
            {selectedQuestion.articleRefs?.length ? (
              <span className={ccStyles.gdprMetaChip}>
                {t(`${GDPR_GE}.articlesShort`, "Articles")}: {selectedQuestion.articleRefs.join(", ")}
              </span>
            ) : null}
          </div>
          <details className={`${ccStyles.gdprAdvancedPanel} mt-3`}>
            <summary className={ccStyles.gdprAdvancedSummary}>{t(`${GDPR_GE}.additionalDetails`, "Additional details")}</summary>
            <div className={`${ccStyles.gdprAdvancedBody} space-y-2`}>
              {selectedQuestion.deterministicRulesLocalized ? (
                <p>{pickLoc(selectedQuestion.deterministicRulesLocalized, lang)}</p>
              ) : null}
              {selectedQuestion.inheritanceMappingHints ? <p>{pickLoc(selectedQuestion.inheritanceMappingHints, lang)}</p> : null}
              {selectedQuestion.auditorWarnings ? <p>{pickLoc(selectedQuestion.auditorWarnings, lang)}</p> : null}
            </div>
          </details>
        </header>

        <article className={ccStyles.gdprEvidenceCard}>
          <h4 className="text-sm font-semibold text-slate-900">{t(`${GDPR_GE}.evidenceGuidanceTitle`, "Evidence guidance")}</h4>
          <div className="mt-4 space-y-4">
            <section>
              <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${GDPR_GE}.whatToUpload`, "What to upload")}</div>
              <GdprEvidenceChips items={uploadBullets} />
            </section>
            <section className={ccStyles.gdprEvidenceStrongCard}>
              <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${GDPR_GE}.strongEvidence`, "Strong evidence")}</div>
              <GdprEvidenceChips items={strongBullets} />
            </section>
            <section className={ccStyles.gdprEvidenceWeakCard}>
              <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${GDPR_GE}.weakEvidence`, "Weak or incomplete evidence")}</div>
              <GdprEvidenceChips items={weakBullets} />
            </section>
            <section>
              <div className={ccStyles.gdprEvidenceSectionTitle}>{t(`${GDPR_GE}.commonProblems`, "Common problems")}</div>
              <ul className={ccStyles.gdprProblemsList}>
                {commonProblems.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </section>
            {optionalBullets.length ? (
              <details className={ccStyles.gdprAdvancedPanel}>
                <summary className={ccStyles.gdprAdvancedSummary}>
                  {t(`${GDPR_GE}.optionalSupporting`, "Optional supporting materials")}
                </summary>
                <div className="mt-2">
                  <GdprEvidenceChips items={optionalBullets} />
                </div>
              </details>
            ) : null}
          </div>
          <details className={`${ccStyles.gdprAdvancedPanel} mt-4`}>
            <summary className={ccStyles.gdprAdvancedSummary}>{t(`${GDPR_GE}.showAdvancedEvidence`, "Show advanced evidence guidance")}</summary>
            <div className={`${ccStyles.gdprAdvancedBody} mt-2 space-y-1`}>
              {em?.freshnessExpectation ? <p>{pickLoc(em.freshnessExpectation, lang)}</p> : null}
              {em?.evidenceGapWarnings ? <p>{pickLoc(em.evidenceGapWarnings, lang)}</p> : null}
              {em?.ownershipExpectation ? <p>{pickLoc(em.ownershipExpectation, lang)}</p> : null}
              {em?.evidenceCollectionNotes ? <p>{pickLoc(em.evidenceCollectionNotes, lang)}</p> : null}
              {em?.evidenceAntiPatterns ? <p>{pickLoc(em.evidenceAntiPatterns, lang)}</p> : null}
            </div>
          </details>
        </article>

        <article className={ccStyles.metricCard}>
          <div className="text-xs font-semibold text-gray-500">{t("complianceCenterUi.journey.answerLabel", "Answer")}</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {(["YES", "PARTIAL", "NO", "NOT_APPLICABLE"] as const).map((opt) => {
              const label =
                opt === "YES"
                  ? t("complianceCenterUi.journey.answerYes", "Yes")
                  : opt === "PARTIAL"
                    ? t("complianceCenterUi.journey.answerPartial", "Partial")
                    : opt === "NO"
                      ? t("complianceCenterUi.journey.answerNo", "No")
                      : t("complianceCenterUi.journey.answerNa", "Not applicable");
              return (
                <Button
                  key={opt}
                  type="button"
                  size="small"
                  color={answerChoice === opt ? "primary" : "outline"}
                  className={answerChoice === opt ? JOURNEY_PRIMARY_BTN : ""}
                  onClick={() => setAnswerChoice(opt)}
                >
                  {label}
                </Button>
              );
            })}
          </div>
        </article>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="block text-xs">
            <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.businessOwner", "Business owner")}</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={businessOwner}
              onChange={(e) => setBusinessOwner(e.target.value)}
              placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
            />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.technicalOwner", "Technical owner")}</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={technicalOwner}
              onChange={(e) => setTechnicalOwner(e.target.value)}
              placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
            />
          </label>
          <label className="block text-xs">
            <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.securityOwner", "Security owner")}</span>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={securityOwner}
              onChange={(e) => setSecurityOwner(e.target.value)}
              placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
            />
          </label>
        </div>

        <label className="block text-xs">
          <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.assignedUserId", "Assigned owner user ID (optional)")}</span>
          <input
            className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono text-xs"
            value={assignedOwnerUserId}
            onChange={(e) => setAssignedOwnerUserId(e.target.value)}
            placeholder="UUID"
          />
        </label>

        {answerChoice === "NOT_APPLICABLE" ? (
          <label className="block text-xs">
            <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.naJustification", "Not applicable — justification")}</span>
            <textarea
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              rows={2}
              value={naJustification}
              onChange={(e) => setNaJustification(e.target.value)}
              placeholder={t("complianceCenterUi.journey.naJustificationPlaceholder", "Explain scope exclusion (auditors expect a short rationale).")}
            />
          </label>
        ) : null}

        {featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 && sessionId && selectedDomain && selectedQuestion ? (
          <QuestionEvidenceBindingPanel
            sessionId={sessionId}
            domainKey={selectedDomain.key}
            questionKey={selectedQuestion.questionKey}
            canonicalControlCode={selectedQuestion.canonicalControlCode}
            standardCode={journeyStandard}
            answerValue={answerChoice}
            govEvidence={govEvidence}
            onLinkedChange={handleQuestionLinkedEvidenceChange}
          />
        ) : null}

        <div
          className={`rounded-md border border-gray-200 bg-white p-3 text-xs ${featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 && sessionId ? "hidden" : ""}`}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="font-semibold text-gray-800">{t("complianceCenterUi.journey.evidenceWorkbenchTitle", "Evidence links")}</span>
            <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase text-gray-700">
              {t(`complianceCenterUi.journey.evidenceBucket.${evidenceBucket}`, evidenceBucket)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <Link to="/compliance-center/evidence">
              <Button type="button" size="small" color="primary">
                {t("complianceEvidence.linkEvidence", "Link evidence")}
              </Button>
            </Link>
            <Link to="/governance/evidence">
              <Button type="button" size="small" color="outline">
                {t("complianceCenterUi.journey.linkGovernanceEvidence", "Governance evidence library")}
              </Button>
            </Link>
          </div>
          {linkedEvidenceIds.length === 0 ? (
            <p className="mt-2 text-amber-800">{t("complianceCenterUi.journey.noEvidenceLinked", "No evidence linked yet.")}</p>
          ) : (
            <ul className="mt-2 space-y-1">
              {linkedEvidenceIds.map((id) => (
                <li key={id} className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2 py-1 font-mono text-[11px]">
                  <span className="truncate">{id}</span>
                  <button
                    type="button"
                    className="shrink-0 text-red-600 hover:underline"
                    onClick={() => setLinkedEvidenceIds((prev) => prev.filter((x) => x !== id))}
                  >
                    {t("complianceCenterUi.journey.removeEvidenceId", "Remove")}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <input
              className="min-w-[12rem] flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-mono"
              value={newEvidenceIdInput}
              onChange={(e) => setNewEvidenceIdInput(e.target.value)}
              placeholder={t("complianceCenterUi.journey.addEvidenceIdPlaceholder", "Paste evidence UUID")}
            />
            <button
              type="button"
              className="rounded border border-gray-300 px-3 py-1 text-xs"
              onClick={() => {
                const v = newEvidenceIdInput.trim();
                if (!v) return;
                setLinkedEvidenceIds((prev) => (prev.includes(v) ? prev : [...prev, v]));
                setNewEvidenceIdInput("");
              }}
            >
              {t("complianceCenterUi.journey.addEvidenceId", "Add ID")}
            </button>
          </div>
          <div className="mt-3">
            <div className="font-semibold text-gray-700">{t("complianceCenterUi.journey.governanceEvidencePicker", "Link from library (this control)")}</div>
            <input
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
              value={evidenceSearch}
              onChange={(e) => setEvidenceSearch(e.target.value)}
              placeholder={t("complianceCenterUi.journey.governanceEvidenceSearch", "Search title…")}
            />
            {govEvidenceLoading ? (
              <p className="mt-2 text-gray-500">{t("complianceCenterUi.journey.governanceEvidenceLoading", "Loading evidence…")}</p>
            ) : govEvidenceError ? (
              <p className="mt-2 text-amber-800">{govEvidenceError}</p>
            ) : (
              <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto">
                {govEvidence.map((row) => (
                  <li key={row.id} className="flex items-center justify-between gap-2 rounded border border-gray-50 px-2 py-1">
                    <div className="min-w-0">
                      <div className="truncate text-xs font-medium text-gray-900">{row.title || row.id}</div>
                      <div className="text-[10px] text-gray-500">
                        {row.freshnessStatus || row.status || "—"} · {row.id}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="shrink-0 rounded bg-sidebarActive px-2 py-1 text-[10px] text-white"
                      onClick={() => setLinkedEvidenceIds((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]))}
                    >
                      {t("complianceCenterUi.journey.linkThisEvidence", "Link")}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {evidenceWarnings.yesPartialNoEvidence ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            {t("complianceCenterUi.journey.warnGdprEvidenceRequired")}
          </div>
        ) : null}
        {linkedEvidenceIds.length > 0 ? (
          <div className="rounded border border-amber-100 bg-amber-50/70 p-2 text-xs text-amber-900">
            {t("complianceCenterUi.journey.warnGdprInheritedArticle")}
          </div>
        ) : null}
        {answerChoice === "NOT_APPLICABLE" && evidenceWarnings.naNoJustification ? (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900">
            {t("complianceCenterUi.journey.warnNaNoJustification", "Not applicable answers should include a short justification.")}
          </div>
        ) : null}
        {answerChoice === "NO" ? (
          <div className="rounded border border-blue-100 bg-blue-50 p-2 text-xs text-blue-900">
            {t("complianceCenterUi.journey.infoNoRemediation", "NO indicates a gap: remediation is expected even if evidence is optional for this response.")}
          </div>
        ) : null}

        <div className={ccStyles.gdprStickySaveBar}>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="small"
              color="primary"
              className={JOURNEY_PRIMARY_BTN}
              onClick={() => void saveAnswer()}
              disabled={saveState === "saving"}
            >
              {saveState === "saving"
                ? t("complianceCenterUi.journey.saving", "Saving…")
                : t("complianceCenterUi.journey.saveAnswer", "Save answer")}
            </Button>
            {saveState === "saved" ? (
              <span className="self-center text-xs text-green-700">{t("complianceCenterUi.journey.saved", "Saved.")}</span>
            ) : null}
            {saveState === "error" && saveError ? <span className="self-center text-xs text-red-600">{saveError}</span> : null}
          </div>
        </div>
      </div>
    );
  };

  const renderSoc2EnterpriseQuestionWorkbench = (opts?: { embedded?: boolean }) => {
    if (!selectedQuestion) return null;
    return (
      <Soc2EnterpriseQuestionWorkbench
        embedded={opts?.embedded}
        lang={lang}
        t={t}
        selectedQuestion={selectedQuestion}
        projectionStatus={projectionForSelected?.status}
        evidenceStale={soc2EvidenceStale}
        lastOperationalReviewLabel={soc2LastOperationalReviewLabel}
        inferredActionsCount={inferredActions.length}
        answerChoice={answerChoice}
        setAnswerChoice={setAnswerChoice}
        businessOwner={businessOwner}
        setBusinessOwner={setBusinessOwner}
        technicalOwner={technicalOwner}
        setTechnicalOwner={setTechnicalOwner}
        securityOwner={securityOwner}
        setSecurityOwner={setSecurityOwner}
        assignedOwnerUserId={assignedOwnerUserId}
        setAssignedOwnerUserId={setAssignedOwnerUserId}
        naJustification={naJustification}
        setNaJustification={setNaJustification}
        linkedEvidenceIds={linkedEvidenceIds}
        setLinkedEvidenceIds={setLinkedEvidenceIds}
        newEvidenceIdInput={newEvidenceIdInput}
        setNewEvidenceIdInput={setNewEvidenceIdInput}
        evidenceSearch={evidenceSearch}
        setEvidenceSearch={setEvidenceSearch}
        govEvidence={govEvidence}
        govEvidenceLoading={govEvidenceLoading}
        govEvidenceError={govEvidenceError}
        evidenceWarnings={evidenceWarnings}
        saveState={saveState}
        saveError={saveError}
        onSave={() => void saveAnswer()}
        sessionId={sessionId}
        domainKey={selectedDomain?.key}
        questionKey={selectedQuestion.questionKey}
        standardCode={journeyStandard}
      />
    );
  };

  const renderNis2EnterpriseQuestionWorkbench = (opts?: { embedded?: boolean }) => {
    if (!selectedQuestion) return null;
    return (
      <Nis2EnterpriseQuestionWorkbench
        embedded={opts?.embedded}
        lang={lang}
        t={t}
        selectedDomainKey={selectedDomainKey || ""}
        selectedQuestion={selectedQuestion}
        selectedQuestionPresentation={selectedQuestionPresentation}
        projectionStatus={projectionForSelected?.status}
        evidenceBucket={evidenceBucket}
        inferredActionsCount={inferredActions.length}
        answerChoice={answerChoice}
        setAnswerChoice={setAnswerChoice}
        businessOwner={businessOwner}
        setBusinessOwner={setBusinessOwner}
        technicalOwner={technicalOwner}
        setTechnicalOwner={setTechnicalOwner}
        securityOwner={securityOwner}
        setSecurityOwner={setSecurityOwner}
        assignedOwnerUserId={assignedOwnerUserId}
        setAssignedOwnerUserId={setAssignedOwnerUserId}
        naJustification={naJustification}
        setNaJustification={setNaJustification}
        linkedEvidenceIds={linkedEvidenceIds}
        setLinkedEvidenceIds={setLinkedEvidenceIds}
        newEvidenceIdInput={newEvidenceIdInput}
        setNewEvidenceIdInput={setNewEvidenceIdInput}
        evidenceSearch={evidenceSearch}
        setEvidenceSearch={setEvidenceSearch}
        govEvidence={govEvidence}
        govEvidenceLoading={govEvidenceLoading}
        govEvidenceError={govEvidenceError}
        evidenceWarnings={evidenceWarnings}
        saveState={saveState}
        saveError={saveError}
        onSave={() => void saveAnswer()}
        sessionId={sessionId}
        domainKey={selectedDomain?.key || selectedDomainKey}
        questionKey={selectedQuestion.questionKey}
        standardCode={journeyStandard}
      />
    );
  };

  const renderJourneyQuestionWorkbench = (opts?: { embedded?: boolean }) => {
    if (!selectedQuestion) return null;
    const isGdprJourney = journeyStandard === "GDPR";
    if (isGdprJourney) return renderGdprEnterpriseQuestionWorkbench(opts);
    const isNis2Journey = journeyStandard === "NIS2";
    if (isNis2Journey) return renderNis2EnterpriseQuestionWorkbench(opts);
    const isSoc2Journey = journeyStandard === "SOC2";
    if (isSoc2Journey) return renderSoc2EnterpriseQuestionWorkbench(opts);
    const isIsoJourney = journeyStandard === "ISO27001" || journeyStandard === "ISO42001";
    const enterpriseJourneyContext = isGdprJourney || isIsoJourney || isNis2Journey || isSoc2Journey;
    const wrap = opts?.embedded
      ? "mt-3 space-y-4 border-t border-gray-100 pt-3"
      : "mt-4 space-y-4 border-t border-gray-100 pt-4";
    return (
      <div className={wrap}>

                    <article className={ccStyles.metricCard}>
                      <h3 className={ccStyles.metricTitle}>
                        {t("complianceCenterUi.journey.currentQuestionLabel", "Current question")}
                      </h3>
                      <p className="mt-2 text-lg font-bold leading-snug text-slate-900">{pickLoc(selectedQuestion.localization, lang)}</p>
                      {isGdprJourney && selectedQuestion.articleRefs?.length ? (
                        <p className="mt-1 text-xs text-gray-700">
                          <span className="font-semibold">{t("complianceCenterUi.journey.gdprArticles", "GDPR articles")}:</span>{" "}
                          {selectedQuestion.articleRefs.join(", ")}
                        </p>
                      ) : null}
                      {enterpriseJourneyContext && selectedQuestion.whyThisMatters ? (
                        <p className="mt-2 text-xs text-gray-700">
                          <span className="font-semibold">{t("complianceCenterUi.journey.whyThisMatters", "Why this matters")}:</span>{" "}
                          {pickLoc(selectedQuestion.whyThisMatters, lang)}
                        </p>
                      ) : null}
                      {enterpriseJourneyContext && (selectedQuestion.reviewCadence || selectedQuestion.reviewCadenceLocalized) ? (
                        <p className="mt-1 text-xs text-gray-600">
                          <span className="font-semibold">{t("complianceCenterUi.journey.reviewCadence", "Review cadence")}:</span>{" "}
                          {isIsoJourney && selectedQuestion.reviewCadenceLocalized
                            ? pickLoc(selectedQuestion.reviewCadenceLocalized, lang)
                            : selectedQuestion.reviewCadence}
                        </p>
                      ) : null}
                      {enterpriseJourneyContext && selectedQuestion.ownerRoleKey ? (
                        <p className="mt-1 text-xs text-gray-600">
                          <span className="font-semibold">{t("complianceCenterUi.journey.ownerRoleKey", "Owner role key")}:</span>{" "}
                          {selectedQuestion.ownerRoleKey}
                        </p>
                      ) : null}
                      {enterpriseJourneyContext && selectedQuestion.deterministicRulesLocalized ? (
                        <p className="mt-2 text-xs text-gray-700">
                          <span className="font-semibold">{t("complianceCenterUi.journey.deterministicRules", "Deterministic evaluation hints")}:</span>{" "}
                          {pickLoc(selectedQuestion.deterministicRulesLocalized, lang)}
                        </p>
                      ) : null}
                      {isGdprJourney && selectedQuestion.inheritanceMappingHints ? (
                        <p className="mt-1 text-xs text-amber-900">
                          <span className="font-semibold">{t("complianceCenterUi.journey.inheritanceMappingHints", "Inheritance limitations (hints)")}:</span>{" "}
                          {pickLoc(selectedQuestion.inheritanceMappingHints, lang)}
                        </p>
                      ) : null}
                      {isGdprJourney && selectedQuestion.auditorWarnings ? (
                        <p className="mt-1 text-xs text-amber-950">
                          <span className="font-semibold">{t("complianceCenterUi.journey.auditorWarnings", "Auditor warning")}:</span>{" "}
                          {pickLoc(selectedQuestion.auditorWarnings, lang)}
                        </p>
                      ) : null}
                      {isGdprJourney && selectedQuestion.articleTraceability ? (
                        <div className="mt-3 space-y-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-3 text-xs text-slate-800">
                          <div className="font-semibold text-slate-900">
                            {t("complianceCenterUi.journey.articleTraceabilityTitle", "Article traceability (operational)")}
                          </div>
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.primaryArticle", "Primary article")}:</span>{" "}
                            {selectedQuestion.articleTraceability.primaryArticle}
                            {selectedQuestion.articleTraceability.secondaryArticles?.length ? (
                              <>
                                {" "}
                                <span className="font-semibold">{t("complianceCenterUi.journey.secondaryArticles", "Secondary")}:</span>{" "}
                                {selectedQuestion.articleTraceability.secondaryArticles.join(", ")}
                              </>
                            ) : null}
                          </p>
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.operationalObligation", "Operational obligation")}:</span>{" "}
                            {pickLoc(selectedQuestion.articleTraceability.operationalObligationSummary, lang)}
                          </p>
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.regulatorConcern", "Regulator concern")}:</span>{" "}
                            {pickLoc(selectedQuestion.articleTraceability.regulatorConcernSummary, lang)}
                          </p>
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.auditFocusAreas", "Audit focus areas")}:</span>{" "}
                            {pickLoc(selectedQuestion.articleTraceability.auditFocusAreas, lang)}
                          </p>
                        </div>
                      ) : null}
                    </article>
                    <details className={ccStyles.legacyComplianceDetails}>
                      <summary className={ccStyles.legacyComplianceSummary}>
                        {t("complianceCenterUi.journey.auditorPackGuidanceSummary", "Auditor guidance & expected evidence")}
                      </summary>
                      <div className="mt-3 space-y-2 text-xs text-gray-700">
                        {selectedPackQ ? (
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.auditorGuidance", "Auditor guidance")}:</span>{" "}
                            {pickPackLoc(selectedPackQ, lang, "auditorGuidance")}
                          </p>
                        ) : null}
                        {selectedPackQ ? (
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.expectedEvidence", "Expected evidence")}:</span>{" "}
                            {pickPackLoc(selectedPackQ, lang, "evidenceCollectionGuidance")}
                          </p>
                        ) : (
                          <p>
                            <span className="font-semibold">{t("complianceCenterUi.journey.expectedEvidence", "Expected evidence")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceHint, lang)}
                          </p>
                        )}
                      </div>
                    </details>

                    {selectedQuestion?.canonicalControlCode &&
                    (controlsByCode[selectedQuestion.canonicalControlCode]?.evidenceRequirements || []).length ? (
                      <div className="rounded-md border border-gray-100 bg-gray-50 p-3 text-xs text-gray-800">
                        <div className="font-semibold text-gray-700">
                          {t("complianceCenterUi.journey.expectedEvidenceCatalog", "Canonical evidence requirements")}
                        </div>
                        <ul className="mt-2 list-inside list-disc space-y-1">
                          {(controlsByCode[selectedQuestion.canonicalControlCode].evidenceRequirements || []).map(
                            (er: { required?: boolean; localization?: LocTriple; requirementKey?: string }, idx: number) => (
                              <li key={idx}>
                                <span className={er.required ? "font-semibold text-gray-900" : "text-gray-600"}>
                                  {er.required
                                    ? t("complianceCenterUi.journey.evidenceTierRequired", "Required")
                                    : t("complianceCenterUi.journey.evidenceTierOptional", "Optional")}
                                  : {pickLoc(er.localization, lang) || er.requirementKey || "—"}
                                </span>
                              </li>
                            ),
                          )}
                        </ul>
                      </div>
                    ) : null}

                    <div className="rounded-md border border-blue-100 bg-blue-50/60 p-3 text-xs text-gray-800">
                      <div className="font-semibold text-gray-800">
                        {t("complianceCenterUi.journey.evidenceTiersTitle", "Evidence expectations (guidance)")}
                      </div>
                      {enterpriseJourneyContext &&
                      selectedQuestion.evidenceModel &&
                      typeof selectedQuestion.evidenceModel === "object" ? (
                        isIsoJourney ? (
                          <ul className="mt-2 list-inside list-disc space-y-1">
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.expectedEvidenceLabel", "Expected evidence")}:</span>{" "}
                              {pickLoc(selectedQuestion.evidenceModel.expectedEvidence as LocTriple, lang)}
                            </li>
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.strongIndicators", "Strong evidence indicators")}:</span>{" "}
                              {pickLoc(selectedQuestion.evidenceModel.strongEvidenceIndicators as LocTriple, lang)}
                            </li>
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.weakIndicators", "Weak evidence indicators")}:</span>{" "}
                              {pickLoc(selectedQuestion.evidenceModel.weakEvidenceIndicators as LocTriple, lang)}
                            </li>
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.auditorExpectation", "Auditor expectation")}:</span>{" "}
                              {pickLoc(selectedQuestion.evidenceModel.auditorExpectation as LocTriple, lang)}
                            </li>
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.freshnessLabel", "Freshness")}:</span>{" "}
                              {pickLoc(selectedQuestion.evidenceModel.freshnessExpectation as LocTriple, lang)}
                            </li>
                            {typeof selectedQuestion.evidenceModel.freshnessDays === "number" ? (
                              <li>
                                <span className="font-semibold">
                                  {t("complianceCenterUi.journey.evidenceFreshnessWindow", "Evidence freshness window (days)")}:
                                </span>{" "}
                                {String(selectedQuestion.evidenceModel.freshnessDays)}
                              </li>
                            ) : null}
                          </ul>
                        ) : (
                        <ul className="mt-2 list-inside list-disc space-y-1">
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.expectedEvidenceLabel", "Expected evidence")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.expectedEvidence as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.strongIndicators", "Strong evidence indicators")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.strongEvidenceIndicators as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.weakIndicators", "Weak evidence indicators")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.weakEvidenceIndicators as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.freshnessLabel", "Freshness")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.freshnessExpectation as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.gapWarningsLabel", "Evidence gap warnings")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.evidenceGapWarnings as LocTriple, lang)}
                          </li>
                          {typeof selectedQuestion.evidenceModel.freshnessDays === "number" ? (
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.evidenceFreshnessWindow", "Evidence freshness window (days)")}:</span>{" "}
                              {String(selectedQuestion.evidenceModel.freshnessDays)}
                            </li>
                          ) : null}
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.requiredEvidenceLabel", "Required evidence types")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.requiredEvidence as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.optionalEvidenceLabel", "Optional supporting evidence")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.optionalEvidence as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.staleEvidenceConditions", "Stale evidence conditions")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.staleConditions as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.missingEvidenceWarnings", "Missing evidence warnings")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.missingEvidenceWarnings as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.ownershipExpectation", "Ownership expectation")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.ownershipExpectation as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.evidenceCollectionNotes", "Evidence collection notes")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.evidenceCollectionNotes as LocTriple, lang)}
                          </li>
                          <li>
                            <span className="font-semibold">{t("complianceCenterUi.journey.evidenceAntiPatterns", "Common weak evidence / anti-patterns")}:</span>{" "}
                            {pickLoc(selectedQuestion.evidenceModel.evidenceAntiPatterns as LocTriple, lang)}
                          </li>
                          {selectedQuestion.evidenceModel.evidenceQualityRubric &&
                          typeof selectedQuestion.evidenceModel.evidenceQualityRubric === "object" ? (
                            <li>
                              <span className="font-semibold">{t("complianceCenterUi.journey.evidenceQualityRubric", "Evidence quality rubric (not a compliance score)")}:</span>{" "}
                              {pickLoc(
                                (selectedQuestion.evidenceModel.evidenceQualityRubric as { description?: LocTriple }).description,
                                lang,
                              )}
                            </li>
                          ) : null}
                        </ul>
                        )
                      ) : (
                        <ul className="mt-2 list-inside list-disc space-y-1">
                          <li>{t("complianceCenterUi.journey.evidenceStrong", "Strong: signed attestations, IdP/CMDB exports, DR test reports.")}</li>
                          <li>{t("complianceCenterUi.journey.evidenceWeak", "Weak: informal screenshots without metadata or scope.")}</li>
                          <li>{t("complianceCenterUi.journey.evidenceAuditGrade", "Audit-grade: third-party SOC/ISO artifacts mapped to the control.")}</li>
                          <li>
                            {t(
                              "complianceCenterUi.journey.evidenceFreshnessHint",
                              "Freshness: renew before organizational SLA (commonly 90 days) when projections stay partial.",
                            )}
                          </li>
                        </ul>
                      )}
                    </div>

                    <article className={ccStyles.metricCard}>
                    <div>
                      <div className="text-xs font-semibold text-gray-500">{t("complianceCenterUi.journey.answerLabel", "Answer")}</div>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {(["YES", "PARTIAL", "NO", "NOT_APPLICABLE"] as const).map((opt) => {
                          const label =
                            opt === "YES"
                              ? t("complianceCenterUi.journey.answerYes", "Yes")
                              : opt === "PARTIAL"
                                ? t("complianceCenterUi.journey.answerPartial", "Partial")
                                : opt === "NO"
                                  ? t("complianceCenterUi.journey.answerNo", "No")
                                  : t("complianceCenterUi.journey.answerNa", "Not applicable");
                          return (
                            <Button
                              key={opt}
                              type="button"
                              size="small"
                              color={answerChoice === opt ? "primary" : "outline"}
                              className={answerChoice === opt ? JOURNEY_PRIMARY_BTN : ""}
                              onClick={() => setAnswerChoice(opt)}
                            >
                              {label}
                            </Button>
                          );
                        })}
                      </div>
                    </div>
                    </article>

                    <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                      <label className="block text-xs">
                        <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.businessOwner", "Business owner")}</span>
                        <input
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                          value={businessOwner}
                          onChange={(e) => setBusinessOwner(e.target.value)}
                          placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.technicalOwner", "Technical owner")}</span>
                        <input
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                          value={technicalOwner}
                          onChange={(e) => setTechnicalOwner(e.target.value)}
                          placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
                        />
                      </label>
                      <label className="block text-xs">
                        <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.securityOwner", "Security owner")}</span>
                        <input
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                          value={securityOwner}
                          onChange={(e) => setSecurityOwner(e.target.value)}
                          placeholder={t("complianceCenterUi.journey.ownerPlaceholder", "Name or reference")}
                        />
                      </label>
                    </div>

                    <label className="block text-xs">
                      <span className="font-semibold text-gray-600">{t("complianceCenterUi.journey.assignedUserId", "Assigned owner user ID (optional)")}</span>
                      <input
                        className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm font-mono text-xs"
                        value={assignedOwnerUserId}
                        onChange={(e) => setAssignedOwnerUserId(e.target.value)}
                        placeholder="UUID"
                      />
                    </label>

                    {answerChoice === "NOT_APPLICABLE" ? (
                      <label className="block text-xs">
                        <span className="font-semibold text-gray-600">
                          {t("complianceCenterUi.journey.naJustification", "Not applicable — justification")}
                        </span>
                        <textarea
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                          rows={2}
                          value={naJustification}
                          onChange={(e) => setNaJustification(e.target.value)}
                          placeholder={t(
                            "complianceCenterUi.journey.naJustificationPlaceholder",
                            "Explain scope exclusion (auditors expect a short rationale).",
                          )}
                        />
                      </label>
                    ) : null}

                    {featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 && sessionId && selectedDomain && selectedQuestion ? (
                      <QuestionEvidenceBindingPanel
                        sessionId={sessionId}
                        domainKey={selectedDomain.key}
                        questionKey={selectedQuestion.questionKey}
                        canonicalControlCode={selectedQuestion.canonicalControlCode}
                        standardCode={journeyStandard}
                        answerValue={answerChoice}
                        govEvidence={govEvidence}
                        onLinkedChange={handleQuestionLinkedEvidenceChange}
                      />
                    ) : null}

                    <div
                      className={`rounded-md border border-gray-200 bg-white p-3 text-xs ${
                        featureFlags.COMPLIANCE_EVIDENCE_BINDING_V1 && sessionId ? "hidden" : ""
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="font-semibold text-gray-800">{t("complianceCenterUi.journey.evidenceWorkbenchTitle", "Evidence links")}</span>
                        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium uppercase text-gray-700">
                          {t(`complianceCenterUi.journey.evidenceBucket.${evidenceBucket}`, evidenceBucket)}
                        </span>
                      </div>
                      {(() => {
                        const er = selectedPackQ?.evidenceRuleLocalization;
                        const body = stringifyLocLeaf(er?.[lang]?.body) || stringifyLocLeaf(er?.en?.body);
                        if (!body) return null;
                        const title = stringifyLocLeaf(er?.[lang]?.title) || stringifyLocLeaf(er?.en?.title);
                        return (
                          <p className="mt-2 text-gray-700">
                            {title ? <span className="font-semibold">{title}: </span> : null}
                            {body}
                          </p>
                        );
                      })()}
                      <div className="mt-2 flex flex-wrap gap-2">
                        <Link className="text-sidebarActive underline" to="/compliance-center/evidence">
                          {t("complianceEvidence.linkEvidence", "Link evidence")}
                        </Link>
                        <span className="text-gray-400">|</span>
                        <Link className="text-sidebarActive underline" to="/governance/evidence">
                          {t("complianceCenterUi.journey.linkGovernanceEvidence", "Governance evidence library")}
                        </Link>
                      </div>
                      {linkedEvidenceIds.length === 0 ? (
                        <p className="mt-2 text-amber-800">{t("complianceCenterUi.journey.noEvidenceLinked", "No evidence linked yet.")}</p>
                      ) : (
                        <ul className="mt-2 space-y-1">
                          {linkedEvidenceIds.map((id) => (
                            <li key={id} className="flex items-center justify-between gap-2 rounded border border-gray-100 px-2 py-1 font-mono text-[11px]">
                              <span className="truncate">{id}</span>
                              <button
                                type="button"
                                className="shrink-0 text-red-600 hover:underline"
                                onClick={() => setLinkedEvidenceIds((prev) => prev.filter((x) => x !== id))}
                              >
                                {t("complianceCenterUi.journey.removeEvidenceId", "Remove")}
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="mt-3 flex flex-wrap gap-2">
                        <input
                          className="min-w-[12rem] flex-1 rounded border border-gray-300 px-2 py-1 text-sm font-mono"
                          value={newEvidenceIdInput}
                          onChange={(e) => setNewEvidenceIdInput(e.target.value)}
                          placeholder={t("complianceCenterUi.journey.addEvidenceIdPlaceholder", "Paste evidence UUID")}
                        />
                        <button
                          type="button"
                          className="rounded border border-gray-300 px-3 py-1 text-xs"
                          onClick={() => {
                            const v = newEvidenceIdInput.trim();
                            if (!v) return;
                            setLinkedEvidenceIds((prev) => (prev.includes(v) ? prev : [...prev, v]));
                            setNewEvidenceIdInput("");
                          }}
                        >
                          {t("complianceCenterUi.journey.addEvidenceId", "Add ID")}
                        </button>
                      </div>
                      <div className="mt-3">
                        <div className="font-semibold text-gray-700">{t("complianceCenterUi.journey.governanceEvidencePicker", "Link from library (this control)")}</div>
                        <input
                          className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm"
                          value={evidenceSearch}
                          onChange={(e) => setEvidenceSearch(e.target.value)}
                          placeholder={t("complianceCenterUi.journey.governanceEvidenceSearch", "Search title…")}
                        />
                        {govEvidenceLoading ? (
                          <p className="mt-2 text-gray-500">{t("complianceCenterUi.journey.governanceEvidenceLoading", "Loading evidence…")}</p>
                        ) : govEvidenceError ? (
                          <p className="mt-2 text-amber-800">{govEvidenceError}</p>
                        ) : (
                          <ul className="mt-2 max-h-36 space-y-1 overflow-y-auto">
                            {govEvidence.map((row) => (
                              <li key={row.id} className="flex items-center justify-between gap-2 rounded border border-gray-50 px-2 py-1">
                                <div className="min-w-0">
                                  <div className="truncate text-xs font-medium text-gray-900">{row.title || row.id}</div>
                                  <div className="text-[10px] text-gray-500">
                                    {row.freshnessStatus || row.status || "—"} · {row.id}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="shrink-0 rounded bg-sidebarActive px-2 py-1 text-[10px] text-white"
                                  onClick={() =>
                                    setLinkedEvidenceIds((prev) => (prev.includes(row.id) ? prev : [...prev, row.id]))
                                  }
                                >
                                  {t("complianceCenterUi.journey.linkThisEvidence", "Link")}
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </div>

                    {evidenceWarnings.yesPartialNoEvidence ? (
                      <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                        {isGdprJourney
                          ? t("complianceCenterUi.journey.warnGdprEvidenceRequired")
                          : t(
                              "complianceCenterUi.journey.warnYesPartialNoEvidence",
                              "Positive answer without linked evidence — attach records from Evidence Hub or lower the answer.",
                            )}
                      </div>
                    ) : null}
                    {journeyStandard === "GDPR" && linkedEvidenceIds.length > 0 ? (
                      <div className="rounded border border-amber-200 bg-amber-50/80 p-2 text-xs text-amber-900">
                        {t("complianceCenterUi.journey.warnGdprInheritedArticle")}
                      </div>
                    ) : null}
                    {answerChoice === "NOT_APPLICABLE" && evidenceWarnings.naNoJustification ? (
                      <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-900">
                        {t("complianceCenterUi.journey.warnNaNoJustification", "Not applicable answers should include a short justification.")}
                      </div>
                    ) : null}
                    {answerChoice === "NO" ? (
                      <div className="rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
                        {t(
                          "complianceCenterUi.journey.infoNoRemediation",
                          "NO indicates a gap: remediation is expected even if evidence is optional for this response.",
                        )}
                      </div>
                    ) : null}

                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="small"
                        color="primary"
                        className={JOURNEY_PRIMARY_BTN}
                        onClick={() => void saveAnswer()}
                        disabled={saveState === "saving"}
                      >
                        {saveState === "saving"
                          ? t("complianceCenterUi.journey.saving", "Saving…")
                          : t("complianceCenterUi.journey.saveAnswer", "Save answer")}
                      </Button>
                      {saveState === "saved" ? (
                        <span className="self-center text-xs text-green-700">{t("complianceCenterUi.journey.saved", "Saved.")}</span>
                      ) : null}
                      {saveState === "error" && saveError ? <span className="self-center text-xs text-red-600">{saveError}</span> : null}
                    </div>
      </div>
    );
  };


  if (loadPhase === "loading" || loadPhase === "idle") {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-600">
        {t("complianceCenterUi.journey.loadingCatalog", "Loading operational journey data…")}
      </div>
    );
  }

  if (loadPhase === "error") {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        <p className="font-semibold">{t("complianceCenterUi.journey.errors.catalogTitle", "Journey data unavailable")}</p>
        <p className="mt-1">{loadError}</p>
      </div>
    );
  }

  return (
    <div id="cc-operational-journey-workspace" className="space-y-4">
      {!controlCentricUxActive ? (
        <div id="cc-journey-intro" className={compliancePageStyles.auditReadinessCard}>
          <h3 className={compliancePageStyles.auditReadinessTitle}>{t("complianceCenterUi.journey.title", "Operational Compliance Journey")}</h3>
          <p className="mt-1 text-sm text-gray-600">
            {t(
              "complianceCenterUi.journey.subtitle",
              "Deterministic human workflow tied to canonical controls. Answers and projections are authoritative; this UI does not use AI as an evaluator.",
            )}
          </p>
        </div>
      ) : null}

      {!standardAllowed ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {t(
            "complianceCenterUi.journey.standardDisabled",
            "Selected standard is not enabled for this organization. Enable it in Overview or ask an admin.",
          )}
        </div>
      ) : null}

      {sessionId && controlCentricUxActive && controlCentricOverview?.progress && !controlCentricCompletionView ? (
        <JourneyCompactMetricsBar progress={controlCentricOverview.progress} />
      ) : null}

      {sessionId && !controlCentricUxActive ? (
        <div id="cc-journey-workbench" className={compliancePageStyles.auditReadinessCard}>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t("complianceCenterUi.journey.workbenchStandardTitle", "Standard progress (session + projections)")}
          </div>
          <div className={`mt-3 ${compliancePageStyles.auditReadinessGrid}`}>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricAnswered", "Answered")}</div>
              <div className={compliancePageStyles.auditReadinessValue}>
                {workbenchStats.answered}/{workbenchStats.totalQ}
              </div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricUnanswered", "Unanswered")}</div>
              <div className={compliancePageStyles.auditReadinessValue}>{workbenchStats.unanswered}</div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricEvidenceLinked", "Evidence-linked answers")}</div>
              <div className={compliancePageStyles.auditReadinessValue}>{workbenchStats.evidenceLinkedAnswers}</div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricEvidenceGaps", "YES/PARTIAL without evidence")}</div>
              <div className={`${compliancePageStyles.auditReadinessValue} text-amber-800`}>{workbenchStats.evidenceMissingAnswers}</div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricCompliant", "Compliant controls")}</div>
              <div className={`${compliancePageStyles.auditReadinessValue} text-green-800`}>{workbenchStats.compliant}</div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricPartial", "Partial")}</div>
              <div className={`${compliancePageStyles.auditReadinessValue} text-amber-800`}>{workbenchStats.partial}</div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricNonCompliant", "Non-compliant")}</div>
              <div className={`${compliancePageStyles.auditReadinessValue} text-red-800`}>{workbenchStats.nonCompliant}</div>
            </div>
            <div>
              <div className={compliancePageStyles.auditReadinessLabel}>{t("complianceCenterUi.journey.metricPendingEvidence", "Pending evidence")}</div>
              <div className={compliancePageStyles.auditReadinessValue}>{workbenchStats.pendingEvidence}</div>
            </div>
          </div>
        </div>
      ) : null}

      <div id="cc-journey-session-cards" className="space-y-4">
        {controlCentricUxActive ? (
          !sessionId ? (
            <article className={`flex flex-col ${compliancePageStyles.auditReadinessCard}`}>
              <h4 className="text-sm font-semibold text-slate-900">
                {t("controlCentric.session.startTitle", "{{standard}} operational execution", {
                  standard: standardLabel(journeyStandard),
                })}
              </h4>
              <p className="mt-1 flex-1 text-sm text-slate-600">
                {t(
                  "controlCentric.session.startBody",
                  "Start or resume an execution session to work through controls, shared evidence, and assessor answers.",
                )}
              </p>
              <Button
                type="button"
                data-testid="journey-open-session-setup"
                size="small"
                color="primary"
                className={`mt-3 w-fit ${JOURNEY_PRIMARY_BTN}`}
                disabled={!standardAllowed}
                onClick={() => {
                  setSessionModalOpen(true);
                  void loadSessions();
                }}
              >
                {t("controlCentric.session.continueOrCreate", "Continue or create session")}
              </Button>
            </article>
          ) : null
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <article className={`flex flex-col ${compliancePageStyles.auditReadinessCard}`}>
              <h4 className="text-sm font-semibold text-slate-900">{t("complianceCenterUi.journey.sessionSignalCardTitle", "Session setup")}</h4>
              <p className="mt-1 flex-1 text-xs text-slate-600">{sessionCardSubtitle}</p>
              <Button
                type="button"
                data-testid="journey-open-session-setup"
                size="small"
                color="primary"
                className={`mt-3 w-fit ${JOURNEY_PRIMARY_BTN}`}
                disabled={!standardAllowed}
                onClick={() => {
                  setSessionModalOpen(true);
                  void loadSessions();
                }}
              >
                {t("complianceCenterUi.journey.openSessionSetup", "Open session setup")}
              </Button>
            </article>
            <article className={`flex flex-col ${compliancePageStyles.auditReadinessCard}`}>
              <h4 className="text-sm font-semibold text-slate-900">{t("complianceCenterUi.journey.browseDomainsCardTitle", "Browse domains")}</h4>
              <p className="mt-1 flex-1 text-xs text-slate-600">{domainBrowseSubtitle}</p>
              <Button
                type="button"
                data-testid="journey-open-domains-modal"
                size="small"
                color="primary"
                className={`mt-3 w-fit ${JOURNEY_PRIMARY_BTN}`}
                onClick={() => setDomainsModalOpen(true)}
              >
                {t("complianceCenterUi.journey.openDomainsButton", "Open domains")}
              </Button>
            </article>
          </div>
        )}

        <div className="mx-auto w-full max-w-5xl space-y-4">
          {!sessionId ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 p-6 text-sm text-slate-700">
              {t("complianceCenterUi.journey.noSession", "Create or select a session to answer questions and update readiness projections.")}
            </div>
          ) : (
            <>
              {!controlCentricCompletionView ? (
                <div id="cc-journey-recompute-strip" className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
                  <Button
                    type="button"
                    size="small"
                    color="primary"
                    className={JOURNEY_PRIMARY_BTN}
                    onClick={() => void runRecompute()}
                    disabled={recomputeState === "running"}
                  >
                    {recomputeState === "running"
                      ? t("complianceCenterUi.journey.recomputing", "Recomputing…")
                      : t("complianceCenterUi.journey.recompute", "Recompute readiness")}
                  </Button>
                  {recomputeState === "error" && recomputeError ? (
                    <span className="text-xs text-red-600">
                      {t("complianceCenterUi.journey.recomputeFailed", "Last recompute failed.")} {recomputeError}
                    </span>
                  ) : null}
                  {lastRecomputeOkAt ? (
                    <span className="text-xs text-green-700">
                      {t("complianceCenterUi.journey.recomputeCompleted", "Recompute completed")}: {formatIsoDate(lastRecomputeOkAt)}
                    </span>
                  ) : null}
                </div>
              ) : null}

              {controlCentricUxActive ? (
                <ControlCentricWorkflowWorkspace
                  sessionId={sessionId}
                  standardCode={journeyStandard}
                  lang={lang}
                  domainKey={soc2EvidenceDomainKey}
                  navigationMode={navMode}
                  govEvidence={govEvidence}
                  formatDate={(iso) => (iso ? formatIsoDate(iso) : "")}
                  onSaveQuestionAnswer={saveControlCentricQuestion}
                  onOverviewChange={setControlCentricOverview}
                  onCompletedChange={handleControlCentricCompletedChange}
                  onSwitchSession={openSessionSelector}
                  onNavigationModeChange={setNavMode}
                  initialControlKey={remediationControlKey}
                  remediationMode={remediationMode}
                />
              ) : null}

              {!controlCentricUxActive ? (
              <div id="cc-journey-questions-panel" className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <h4 className="text-sm font-semibold text-slate-900">
                  {t("complianceCenterUi.journey.questionsTitle", "Questions")} — {selectedDomain ? pickLoc(selectedDomain.localization, lang) : "—"}
                </h4>
                {navMode === "SEQUENTIAL" && flatJourneySteps.length ? (
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      disabled={sequentialStepIndex <= 0}
                      onClick={() => goJourneyFlatStep(Math.max(0, sequentialStepIndex - 1))}
                    >
                      {t("complianceCenterUi.journey.sequentialPrev", "Previous")}
                    </Button>
                    <span className="text-xs font-semibold text-slate-700">
                      {t("complianceCenterUi.journey.sequentialPosition", "Step {{current}} of {{total}}", {
                        current: sequentialStepIndex + 1,
                        total: flatJourneySteps.length,
                      })}
                    </span>
                    <Button
                      type="button"
                      size="small"
                      color="primary"
                      className={JOURNEY_PRIMARY_BTN}
                      disabled={sequentialStepIndex >= flatJourneySteps.length - 1}
                      onClick={() => goJourneyFlatStep(Math.min(flatJourneySteps.length - 1, sequentialStepIndex + 1))}
                    >
                      {t("complianceCenterUi.journey.sequentialNext", "Next")}
                    </Button>
                  </div>
                ) : null}
                {navMode !== "SEQUENTIAL" ? (
                  <div className={`mt-3 ${ccStyles.metricsGrid}`}>
                    {(selectedDomain?.questions || []).map((q) => {
                      const isOpen = selectedQuestionKey === q.questionKey;
                      const title = pickLoc(q.localization, lang);
                      const shortTitle = title.length > 120 ? `${title.slice(0, 120)}…` : title;
                      const row = session?.responses?.find(
                        (x: any) => x.questionKey === q.questionKey && String(x.domainKey) === String(selectedDomain?.key),
                      );
                      const pl =
                        row?.answerPayload && typeof row.answerPayload === "object"
                          ? (row.answerPayload as Record<string, string>)
                          : {};
                      const projRow = projections.find((p) => p.control?.code === q.canonicalControlCode);
                      const pendK = selectedDomain ? `${selectedDomain.key}::${q.questionKey}` : "";
                      const pendingRecompute = Boolean(sessionId && selectedDomain && pendingRecomputeKeys.has(pendK));
                      const presentation =
                        sessionId && selectedDomain
                          ? resolveJourneyQuestionRowPresentation({
                              answerValue: row?.answerValue,
                              evidenceIds: row?.evidenceIds,
                              linkedEvidence: row?.linkedEvidence,
                              evidenceSufficiencyState: row?.evidenceSufficiency?.state,
                              naJustification: String(pl.naJustification || pl.notApplicableRationale || ""),
                              projectionStatus: projRow?.status,
                              evidenceCompleteness: projRow?.evidenceCompleteness,
                              pendingRecompute,
                              standardCode: journeyStandard,
                              articleRefs: q.articleRefs,
                              evidenceModel: q.evidenceModel as Record<string, unknown> | undefined,
                            })
                          : {
                              riskState: "NEUTRAL" as const,
                              accent: "neutral" as const,
                              pendingRecompute: false,
                              reasonCode: null as string | null,
                              reasonKey: null as string | null,
                            };
                      const accent = presentation.accent;
                      const riskClass =
                        accent === "green"
                          ? ccStyles.questionPickCardRiskGreen
                          : accent === "amber"
                            ? ccStyles.questionPickCardRiskAmber
                            : accent === "red"
                              ? ccStyles.questionPickCardRiskRed
                              : "";
                      const badgeTone =
                        accent === "green"
                          ? "bg-emerald-100 text-emerald-900"
                          : accent === "amber"
                            ? "bg-amber-100 text-amber-900"
                            : accent === "red"
                              ? "bg-red-100 text-red-900"
                              : "bg-slate-100 text-slate-700";
                      const hintTitle = presentation.reasonKey ? t(presentation.reasonKey) : undefined;
                      return (
                        <details
                          key={q.questionKey}
                          className={`${ccStyles.questionPickCard} ${riskClass} ${isOpen ? ccStyles.questionPickCardActive : ""}`.trim()}
                          open={isOpen}
                          onToggle={(e) => {
                            const el = e.currentTarget as HTMLDetailsElement;
                            if (el.open) setSelectedQuestionKey(q.questionKey);
                            else if (selectedQuestionKey === q.questionKey) setSelectedQuestionKey(null);
                          }}
                        >
                          <summary className="flex cursor-pointer list-none items-start gap-2 [&::-webkit-details-marker]:hidden">
                            <span className={`min-w-0 flex-1 ${ccStyles.questionPickTitle}`}>{shortTitle}</span>
                            <span className="flex shrink-0 flex-col items-end gap-1 sm:flex-row sm:items-center">
                              {presentation.pendingRecompute ? (
                                <span
                                  className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-950"
                                  title={t(
                                    "complianceCenterUi.journey.questionRow.pendingRecompute",
                                    "This answer was saved, but projections may not have refreshed yet.",
                                  )}
                                >
                                  {t("complianceCenterUi.journey.questionRow.pendingRecomputeBadge", "Pending recompute")}
                                </span>
                              ) : null}
                              <span
                                className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${badgeTone}`}
                                title={hintTitle}
                              >
                                {presentation.riskState === "NEUTRAL"
                                  ? t("complianceCenterUi.journey.questionRow.riskLabelNotAnswered", "Not answered")
                                  : presentation.riskState === "GREEN"
                                    ? t("complianceCenterUi.journey.questionRow.riskLabelOnTrack", "On track")
                                    : presentation.riskState === "AMBER"
                                      ? t("complianceCenterUi.journey.questionRow.riskLabelNeedsAttention", "Needs attention")
                                      : t("complianceCenterUi.journey.questionRow.riskLabelHighRisk", "High risk")}
                              </span>
                            </span>
                          </summary>
                          {isOpen && selectedQuestion?.questionKey === q.questionKey
                            ? renderJourneyQuestionWorkbench({ embedded: true })
                            : null}
                        </details>
                      );
                    })}
                  </div>
                ) : null}
                {session && selectedDomain ? (
                  <p className="mt-2 text-xs text-gray-600">
                    {t("complianceCenterUi.journey.domainScopeStats", "This domain: {{answered}} / {{total}} answered · {{missing}} without answer", {
                      answered: domainQuestionStats.answered,
                      total: domainQuestionStats.total,
                      missing: domainQuestionStats.missingKeys.length,
                    })}
                  </p>
                ) : null}

                {navMode !== "SEQUENTIAL" && (selectedDomain?.questions || []).length > 0 && !selectedQuestion ? (
                  <p className="mt-3 text-sm text-gray-600">
                    {t(
                      "complianceCenterUi.journey.pickQuestionExpandHint",
                      "Expand a question above to answer and link evidence.",
                    )}
                  </p>
                ) : null}
                {navMode !== "SEQUENTIAL" && selectedDomain && !(selectedDomain.questions || []).length ? (
                  <p className="mt-3 text-sm text-gray-500">
                    {t("complianceCenterUi.journey.noQuestions", "No questions in this domain.")}
                  </p>
                ) : null}
                {navMode === "SEQUENTIAL" ? (
                  selectedQuestion ? (
                    renderJourneyQuestionWorkbench()
                  ) : (
                    <p className="mt-3 text-sm text-gray-500">
                      {t("complianceCenterUi.journey.noQuestions", "No questions in this domain.")}
                    </p>
                  )
                ) : null}
              </div>
              ) : null}

              {!controlCentricUxActive ? (
              <>
              <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
                <details id="cc-journey-projection-panel" className={ccStyles.legacyComplianceDetails} open>
                  <summary className={ccStyles.legacyComplianceSummary}>
                    {t("complianceCenterUi.journey.controlPanelTitle", "Canonical control & status")}
                  </summary>
                  <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <h4 className="sr-only">{t("complianceCenterUi.journey.controlPanelTitle", "Canonical control & status")}</h4>
                  {selectedQuestion && projectionForSelected ? (
                    <div className="mt-2 space-y-2 text-sm">
                      <div>
                        <span className="text-gray-500">{t("complianceCenterUi.journey.controlCode", "Code")}:</span>{" "}
                        <span className="font-mono">{selectedQuestion.canonicalControlCode}</span>
                      </div>
                      {journeyStandard === "NIS2" || journeyStandard === "SOC2" ? (
                        <div>
                          <span className="text-gray-500">{t("complianceCenterUi.journey.status", "Status")}:</span>{" "}
                          <span className="font-semibold">
                            {(() => {
                              const tone = deriveGdprAssessmentTone({
                                presentationAccent: selectedQuestionPresentation?.accent,
                                projectionStatus: projectionForSelected?.status,
                              });
                              const ge = journeyStandard === "SOC2" ? SOC2_GE : NIS2_GE;
                              if (tone === "green") return t(`${ge}.operationalGreen`, "Operationally stable");
                              if (tone === "red") return t(`${ge}.operationalRed`, "Immediate action required");
                              return t(`${ge}.operationalAmber`, "Operational attention required");
                            })()}
                          </span>
                        </div>
                      ) : (
                        <div>
                          <span className="text-gray-500">{t("complianceCenterUi.journey.status", "Status")}:</span>{" "}
                          <span className="font-semibold">{projectionForSelected.status}</span>
                        </div>
                      )}
                      {journeyStandard !== "GDPR" && journeyStandard !== "NIS2" && journeyStandard !== "SOC2" ? (
                        <>
                          <div>
                            <span className="text-gray-500">{t("complianceCenterUi.journey.readiness", "Readiness")}:</span>{" "}
                            {Math.round(Number(projectionForSelected.readinessScore ?? 0))}
                          </div>
                          <div>
                            <span className="text-gray-500">{t("complianceCenterUi.journey.evidenceCompleteness", "Evidence completeness")}:</span>{" "}
                            {Math.round(Number(projectionForSelected.evidenceCompleteness ?? 0))}
                          </div>
                        </>
                      ) : null}
                      <div className="text-xs text-gray-700">
                        {(() => {
                          const loc = (projectionForSelected.control as any)?.localization || controlsByCode[selectedQuestion.canonicalControlCode]?.localization;
                          const block = loc?.[lang] || loc?.en;
                          return stringifyLocLeaf(block);
                        })()}
                      </div>
                      <div className="mt-2">
                        <div className="text-xs font-semibold text-gray-600">{t("complianceCenterUi.journey.mappingsTitle", "Standard mappings")}</div>
                        <ul className="mt-1 max-h-28 overflow-y-auto text-xs text-gray-700">
                          {(crosswalkByControl[selectedQuestion.canonicalControlCode] ||
                            projectionForSelected.control?.mappings?.map((m: any) => ({
                              standardCode: m.standardCode,
                              externalRef: m.externalRef,
                            })) ||
                            []
                          ).map((m: any, i: number) => (
                            <li key={i}>
                              {m.standardCode}: {m.externalRef || "—"}
                            </li>
                          ))}
                        </ul>
                      </div>
                      {journeyStandard === "SOC2" && selectedQuestion ? (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs leading-snug text-slate-700">{t(`${SOC2_GE}.inheritanceCompact`)}</p>
                          {selectedQuestion.inheritanceMappingHints ? (
                            <details className={ccStyles.gdprAdvancedPanel}>
                              <summary className={ccStyles.gdprAdvancedSummary}>{t(`${SOC2_GE}.showMappingDetails`)}</summary>
                              <div className={`${ccStyles.gdprAdvancedBody} mt-2`}>
                                <p>{pickLoc(selectedQuestion.inheritanceMappingHints, lang)}</p>
                              </div>
                            </details>
                          ) : null}
                        </div>
                      ) : null}
                      {journeyStandard === "NIS2" && selectedQuestion ? (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs leading-snug text-slate-700">
                            {t(
                              `${NIS2_GE}.inheritanceCompact`,
                              "Supporting security evidence may help demonstrate operational cybersecurity practices, but NIS2 obligations still require organization-specific validation and operational review.",
                            )}
                          </p>
                          {selectedQuestion.inheritanceMappingHints || inheritanceRows.length ? (
                            <details className={ccStyles.gdprAdvancedPanel}>
                              <summary className={ccStyles.gdprAdvancedSummary}>
                                {t(`${NIS2_GE}.showMappingDetails`, "Show operational mapping details")}
                              </summary>
                              <div className={`${ccStyles.gdprAdvancedBody} mt-2 space-y-2`}>
                                {pickLoc(selectedQuestion.inheritanceMappingHints, lang) ? (
                                  <p>{pickLoc(selectedQuestion.inheritanceMappingHints, lang)}</p>
                                ) : null}
                                {inheritanceRows.length ? (
                                  <ul className="space-y-1">
                                    {inheritanceRows.map((m: any) => (
                                      <li key={m.targetStandard}>
                                        <span className="font-medium">{m.targetStandard}</span>
                                        {pickLoc(m.mappingRationale, lang) ? `: ${pickLoc(m.mappingRationale, lang)}` : ""}
                                      </li>
                                    ))}
                                  </ul>
                                ) : null}
                              </div>
                            </details>
                          ) : null}
                        </div>
                      ) : null}
                      {inheritanceRows.length ? (
                        journeyStandard === "GDPR" ? (
                          <div className="mt-3 space-y-2">
                            {inheritanceRows.map((m: any) => {
                              const relTone = inheritanceRelationshipTone(m.relationship);
                              const blurb =
                                inheritanceCompactBlurb(pickLoc(m.limitations, lang) || pickLoc(m.mappingRationale, lang)) ||
                                t(`${GDPR_GE}.inheritancePartialDefault`, "Related controls may support this requirement, but privacy-specific validation is still required.");
                              return (
                                <div key={m.targetStandard} className={ccStyles.gdprInheritanceRow}>
                                  <span className={`${ccStyles.gdprInheritanceBadge} ${gdprToneInheritanceBadgeClass(relTone)}`}>
                                    {m.targetStandard} · {m.relationship || "PARTIAL"}
                                  </span>
                                  <span className="text-xs leading-snug text-slate-700">{blurb}</span>
                                </div>
                              );
                            })}
                            <details className={ccStyles.gdprAdvancedPanel}>
                              <summary className={ccStyles.gdprAdvancedSummary}>
                                {t(`${GDPR_GE}.showInheritanceDetails`, "Show inheritance details")}
                              </summary>
                              <ul className={`${ccStyles.gdprAdvancedBody} mt-2 space-y-2`}>
                                {inheritanceRows.map((m: any) => (
                                  <li key={`${m.targetStandard}-detail`}>
                                    <div className="font-medium text-slate-800">{m.targetStandard}</div>
                                    {pickLoc(m.mappingRationale, lang) ? <p>{pickLoc(m.mappingRationale, lang)}</p> : null}
                                    {pickLoc(m.reviewGuidance, lang) ? <p>{pickLoc(m.reviewGuidance, lang)}</p> : null}
                                    {pickLoc(m.prohibitedAssumptions, lang) ? <p>{pickLoc(m.prohibitedAssumptions, lang)}</p> : null}
                                    {pickLoc(m.residualRequirements, lang) ? <p>{pickLoc(m.residualRequirements, lang)}</p> : null}
                                    {pickLoc(m.auditorWarning, lang) ? <p>{pickLoc(m.auditorWarning, lang)}</p> : null}
                                  </li>
                                ))}
                              </ul>
                            </details>
                          </div>
                        ) : journeyStandard === "NIS2" || journeyStandard === "SOC2" ? null : (
                        <div className="mt-3 rounded-md border border-amber-100 bg-amber-50/50 p-2 text-xs text-gray-800">
                          <div className="font-semibold text-amber-900">
                            {t(
                              "complianceCenterUi.journey.inheritanceTitle",
                              "Cross-standard inheritance (informational — not automatic GDPR satisfaction)",
                            )}
                          </div>
                          <ul className="mt-2 max-h-40 space-y-2 overflow-y-auto">
                            {inheritanceRows.map((m: any) => (
                              <li key={m.targetStandard} className="border-b border-amber-100/80 pb-2 last:border-0">
                                <div className="font-medium text-gray-900">
                                  {m.targetStandard} · {m.relationship || "—"} · {t("complianceCenterUi.journey.confidence", "confidence")}:{" "}
                                  {m.inheritanceConfidence || "—"}
                                </div>
                                <div className="mt-0.5 text-gray-700">{pickLoc(m.limitations, lang)}</div>
                                <div className="mt-0.5 text-gray-700">{pickLoc(m.mappingRationale, lang)}</div>
                                <div className="mt-0.5 text-gray-700">{pickLoc(m.reviewGuidance, lang)}</div>
                                <div className="mt-0.5 font-medium text-amber-950">{pickLoc(m.prohibitedAssumptions, lang)}</div>
                                <div className="mt-0.5 text-gray-700">{pickLoc(m.residualRequirements, lang)}</div>
                                <div className="mt-0.5 text-gray-600">{pickLoc(m.auditorWarning, lang)}</div>
                              </li>
                            ))}
                          </ul>
                        </div>
                        )
                      ) : null}
                    </div>
                  ) : selectedQuestion ? (
                    <p className="mt-2 text-sm text-amber-800">
                      {t("complianceCenterUi.journey.noProjection", "No projection yet for this control. Save an answer or run recompute after evidence verification.")}
                    </p>
                  ) : null}

                  <div id="cc-journey-blockers-explainability" className="mt-4 border-t border-gray-100 pt-3">
                    {journeyStandard === "GDPR" ? (
                      <>
                        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {t(`${GDPR_GE}.assessmentSummaryTitle`, "Assessment summary")}
                        </h5>
                        <div
                          className={`${ccStyles.gdprAssessmentCard} mt-2 ${gdprToneAssessmentClass(
                            deriveGdprAssessmentTone({
                              presentationAccent: selectedQuestionPresentation?.accent,
                              projectionStatus: projectionForSelected?.status,
                            }),
                          )}`}
                        >
                          {(() => {
                            const tone = deriveGdprAssessmentTone({
                              presentationAccent: selectedQuestionPresentation?.accent,
                              projectionStatus: projectionForSelected?.status,
                            });
                            if (tone === "green") return t(`${GDPR_GE}.assessmentGreen`, "This area appears operationally supported based on the current answers and linked records.");
                            if (tone === "red") return t(`${GDPR_GE}.assessmentRed`, "Important operational or documentation gaps were identified for this area and should be addressed.");
                            return t(`${GDPR_GE}.assessmentAmber`, "Additional records, approvals, or updated evidence may be required before this area can be considered fully supported.");
                          })()}
                        </div>
                        {selectedQuestion?.articleTraceability ? (
                          <details className={`${ccStyles.gdprAdvancedPanel} mt-3`}>
                            <summary className={ccStyles.gdprAdvancedSummary}>
                              {t(`${GDPR_GE}.showArticleTraceability`, "Article traceability")}
                            </summary>
                            <div className={`${ccStyles.gdprAdvancedBody} space-y-2`}>
                              <p>
                                {selectedQuestion.articleTraceability.primaryArticle}
                                {selectedQuestion.articleTraceability.secondaryArticles?.length
                                  ? ` · ${selectedQuestion.articleTraceability.secondaryArticles.join(", ")}`
                                  : ""}
                              </p>
                              <p>{pickLoc(selectedQuestion.articleTraceability.operationalObligationSummary, lang)}</p>
                              <p>{pickLoc(selectedQuestion.articleTraceability.regulatorConcernSummary, lang)}</p>
                              <p>{pickLoc(selectedQuestion.articleTraceability.auditFocusAreas, lang)}</p>
                            </div>
                          </details>
                        ) : null}
                        <details className={`${ccStyles.gdprAdvancedPanel} mt-3`}>
                          <summary className={ccStyles.gdprAdvancedSummary}>
                            {t(`${GDPR_GE}.showOperationalMetadata`, "Operational metadata")}
                          </summary>
                          <div className={`${ccStyles.gdprAdvancedBody} space-y-1`}>
                            <p>
                              {t("complianceCenterUi.journey.controlCode", "Code")}: {selectedQuestion?.canonicalControlCode}
                            </p>
                            <p>
                              {t("complianceCenterUi.journey.readiness", "Readiness")}:{" "}
                              {Math.round(Number(projectionForSelected?.readinessScore ?? 0))}
                            </p>
                          </div>
                        </details>
                      </>
                    ) : journeyStandard === "NIS2" || journeyStandard === "SOC2" ? (
                      <>
                        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {t(
                            journeyStandard === "SOC2" ? `${SOC2_GE}.assessmentSummaryTitle` : `${NIS2_GE}.assessmentSummaryTitle`,
                            "Assessment summary",
                          )}
                        </h5>
                        <div
                          className={`${ccStyles.gdprAssessmentCard} mt-2 ${gdprToneAssessmentClass(
                            deriveGdprAssessmentTone({
                              presentationAccent: selectedQuestionPresentation?.accent,
                              projectionStatus: projectionForSelected?.status,
                            }),
                          )}`}
                        >
                          {(() => {
                            const tone = deriveGdprAssessmentTone({
                              presentationAccent: selectedQuestionPresentation?.accent,
                              projectionStatus: projectionForSelected?.status,
                            });
                            const ge = journeyStandard === "SOC2" ? SOC2_GE : NIS2_GE;
                            if (tone === "green") return t(`${ge}.operationalGreen`, "Operationally stable");
                            if (tone === "red") return t(`${ge}.operationalRed`, "Immediate action required");
                            return t(`${ge}.operationalAmber`, "Operational attention required");
                          })()}
                        </div>
                      </>
                    ) : (
                      <>
                        <h5 className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          {t("complianceCenterUi.journey.whyThisStatusTitle", "Why this status? (explainability)")}
                        </h5>
                        <ul className="mt-2 space-y-1 text-xs text-gray-800">
                          {statusExplainLines.map((line, i) => (
                            <li key={i} className="font-mono text-[11px]">
                              {line}
                            </li>
                          ))}
                        </ul>
                        <p className="mt-2 text-xs text-gray-600">
                          {t("complianceCenterUi.journey.missingAnswersLabel", "Missing answers in this domain")}: {domainQuestionStats.missingKeys.length}{" "}
                          · {t("complianceCenterUi.journey.linkedEvidenceCount", "Linked evidence IDs (draft)")}: {linkedEvidenceIds.length}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          {t("complianceCenterUi.journey.lastSessionUpdate", "Last session update")}: {formatIsoDate(session?.updatedAt)}
                        </p>
                        <p className="mt-1 text-xs text-gray-600">
                          {t("complianceCenterUi.journey.relatedRecommendationsCount", "Related API recommendations")}: {apiRecsForControl.length}
                        </p>
                      </>
                    )}
                  </div>
                  </div>
                </details>

                <details className={ccStyles.legacyComplianceDetails} open>
                  <summary className={ccStyles.legacyComplianceSummary}>
                    {t("complianceCenterUi.journey.recommendationsTitle", "Recommendations & remediation")}
                  </summary>
                <div id="cc-journey-recommendations-api" className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  {journeyStandard === "GDPR" && selectedQuestion ? (
                    <div className="mt-2 space-y-4 text-xs text-slate-800">
                      <section>
                        <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {t(`${GDPR_GE}.immediateActions`, "Immediate actions")}
                        </h6>
                        <ul className={ccStyles.gdprRemediationList}>
                          {parseRemediationChecklistItems(
                            pickLoc(selectedQuestion.remediationHint, lang) ||
                              (selectedPackQ?.remediationLocalization?.[lang]?.steps ||
                                selectedPackQ?.remediationLocalization?.en?.steps ||
                                []
                              ).join("; "),
                          ).map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </section>
                      <section>
                        <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {t(`${GDPR_GE}.recommendedImprovements`, "Recommended improvements")}
                        </h6>
                        {limitRecommendationParagraphs(
                          curatedRecommendationText || pickLoc(selectedQuestion.recommendationHint, lang),
                        ).map((para) => (
                          <p key={para} className="mt-2 text-sm leading-relaxed text-slate-700">
                            {para}
                          </p>
                        ))}
                      </section>
                      <section>
                        <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {t(`${GDPR_GE}.suggestedEvidence`, "Suggested evidence")}
                        </h6>
                        <GdprEvidenceChips
                          items={parseSemicolonBulletList(
                            pickLoc(
                              (selectedQuestion.evidenceModel as Record<string, LocTriple> | undefined)?.suggestedArtifacts,
                              lang,
                            ) || pickLoc(selectedQuestion.suggestedArtifactsLocalized, lang),
                          )}
                        />
                      </section>
                      <details className={ccStyles.gdprAdvancedPanel}>
                        <summary className={ccStyles.gdprAdvancedSummary}>
                          {t(`${GDPR_GE}.showAuditorGuidance`, "Auditor guidance")}
                        </summary>
                        <div className={`${ccStyles.gdprAdvancedBody} mt-2 space-y-2`}>
                          <p>{pickPackLoc(selectedPackQ, lang, "auditorGuidance") || pickLoc(selectedQuestion.auditorGuidance, lang)}</p>
                          {domainCuratedScenarios.length ? (
                            <ul className="list-inside list-disc space-y-1">
                              {domainCuratedScenarios.map((s) => (
                                <li key={s.id}>
                                  <span className="font-medium">{pickLoc(s.title, lang)}</span>: {pickLoc(s.detail, lang)}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </details>
                    </div>
                  ) : (journeyStandard === "NIS2" || journeyStandard === "SOC2") && selectedQuestion ? (
                    <div className="mt-2 space-y-4 text-xs text-slate-800">
                      <section>
                        <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {t(
                            journeyStandard === "SOC2" ? `${SOC2_GE}.immediateActions` : `${NIS2_GE}.immediateActions`,
                            "Immediate operational actions",
                          )}
                        </h6>
                        <ul className={ccStyles.gdprRemediationList}>
                          {parseRemediationChecklistItems(
                            pickLoc(selectedQuestion.remediationHint, lang) ||
                              (selectedPackQ?.remediationLocalization?.[lang]?.steps ||
                                selectedPackQ?.remediationLocalization?.en?.steps ||
                                []
                              ).join("; "),
                          ).map((item) => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </section>
                      <section>
                        <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {t(
                            journeyStandard === "SOC2" ? `${SOC2_GE}.recommendedImprovements` : `${NIS2_GE}.recommendedImprovements`,
                            "Recommended operational improvements",
                          )}
                        </h6>
                        {limitRecommendationParagraphs(
                          curatedRecommendationText || pickLoc(selectedQuestion.recommendationHint, lang),
                        ).map((para) => (
                          <p key={para} className="mt-2 text-sm leading-relaxed text-slate-700">
                            {para}
                          </p>
                        ))}
                      </section>
                      <section>
                        <h6 className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                          {t(
                            journeyStandard === "SOC2" ? `${SOC2_GE}.suggestedMaterials` : `${NIS2_GE}.suggestedMaterials`,
                            "Suggested operational materials",
                          )}
                        </h6>
                        <GdprEvidenceChips
                          items={parseSemicolonBulletList(
                            pickLoc(
                              (selectedQuestion.evidenceModel as Record<string, LocTriple> | undefined)?.suggestedArtifacts,
                              lang,
                            ) || pickLoc(selectedQuestion.suggestedArtifactsLocalized, lang),
                          )}
                        />
                      </section>
                      <details className={ccStyles.gdprAdvancedPanel}>
                        <summary className={ccStyles.gdprAdvancedSummary}>
                          {t(`${GDPR_GE}.showAuditorGuidance`, "Auditor guidance")}
                        </summary>
                        <div className={`${ccStyles.gdprAdvancedBody} mt-2 space-y-2`}>
                          <p>{pickPackLoc(selectedPackQ, lang, "auditorGuidance") || pickLoc(selectedQuestion.auditorGuidance, lang)}</p>
                          {domainCuratedScenarios.length ? (
                            <ul className="list-inside list-disc space-y-1">
                              {domainCuratedScenarios.map((s) => (
                                <li key={s.id}>
                                  <span className="font-medium">{pickLoc(s.title, lang)}</span>: {pickLoc(s.detail, lang)}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </div>
                      </details>
                    </div>
                  ) : (
                  <div className="mt-2 space-y-2 text-xs text-gray-800">
                    <p>
                      <span className="font-semibold">{t("complianceCenterUi.journey.recommendation", "Recommendation")} (curated):</span>{" "}
                      {curatedRecommendationText || t("complianceCenterUi.journey.curatedRecommendationFallback", "Follow pack guidance and auditor notes above.")}
                    </p>
                    {domainCuratedScenarios.length ? (
                      <div className="rounded-md border border-gray-100 bg-gray-50 p-2">
                        <div className="font-semibold text-gray-800">
                          {t("complianceCenterUi.journey.domainScenariosTitle", "Domain scenarios (deterministic fallback)")}
                        </div>
                        <ul className="mt-1 list-inside list-disc space-y-1 text-gray-800">
                          {domainCuratedScenarios.map((s) => (
                            <li key={s.id}>
                              <span className="font-medium">{pickLoc(s.title, lang)}</span>
                              {": "}
                              {pickLoc(s.detail, lang)}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                    <p>
                      <span className="font-semibold">{t("complianceCenterUi.journey.remediationTitle", "Remediation")}:</span>{" "}
                      {(selectedPackQ?.remediationLocalization?.[lang]?.steps || selectedPackQ?.remediationLocalization?.en?.steps || []).join(" · ") ||
                        pickLoc(selectedQuestion?.remediationHint, lang)}
                    </p>
                    <p>
                      <span className="font-semibold">{t("complianceCenterUi.journey.auditorGuidance", "Auditor guidance")}:</span>{" "}
                      {pickPackLoc(selectedPackQ, lang, "auditorGuidance")}
                    </p>
                    <p className="text-gray-600">
                      {t("complianceCenterUi.journey.linkedControls", "Linked controls")}: {selectedQuestion?.canonicalControlCode || "—"} ·{" "}
                      {t("complianceCenterUi.journey.linkedStandards", "Standards")}:{" "}
                      {(selectedPackQ?.linkedStandards || selectedPackQ?.evidenceExpectations?.linkedStandards || []).join(", ") || journeyStandard}
                    </p>
                    <div className="flex flex-wrap gap-1 pt-1">
                      {inferredActions.map((a) => (
                        <span key={a} className="rounded bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-800">
                          {t(`complianceCenterUi.journey.actionType.${a}`, a)}
                        </span>
                      ))}
                    </div>
                  </div>
                  )}

                  <div className="mt-3 border-t border-gray-100 pt-2">
                    <div className="text-xs font-semibold text-gray-700">
                      {t("complianceCenterUi.journey.apiRecommendationsHeading", "Persisted recommendations (API)")}
                    </div>
                    {apiRecsForControl.length ? (
                      <ul className="mt-2 max-h-48 space-y-2 overflow-y-auto text-xs">
                        {apiRecsForControl.slice(0, 12).map((r: any) => (
                          <li key={r.id} className="rounded border border-gray-100 p-2">
                            <label className="flex cursor-pointer gap-2">
                              <input
                                type="radio"
                                name="journey-rec"
                                checked={selectedRecommendationId === r.id}
                                onChange={() => setSelectedRecommendationId(String(r.id))}
                              />
                              <span>
                                <span className="font-medium">{stringifyLocLeaf(r.title) || r.id}</span>
                                <div className="text-gray-600">
                                  {t("complianceCenterUi.journey.priority", "Priority")}: {r.priority || "—"} ·{" "}
                                  {t("complianceCenterUi.journey.severity", "Severity")}: {r.severity || "—"} · {r.status}
                                </div>
                                {r.controlCode ? (
                                  <div className="text-gray-600">
                                    {t("complianceCenterUi.journey.linkedControls", "Linked controls")}: {r.controlCode}
                                  </div>
                                ) : null}
                                {r.ownerName ? (
                                  <div className="text-gray-600">
                                    {t("complianceCenterUi.journey.suggestedOwner", "Suggested owner")}: {r.ownerName}
                                  </div>
                                ) : null}
                                {r.dueDate ? (
                                  <div className="text-gray-600">
                                    {t("complianceCenterUi.journey.suggestedDueDate", "Due")}: {formatIsoDate(r.dueDate)}
                                  </div>
                                ) : null}
                                {stringifyLocLeaf(r.description) ? (
                                  <div className="mt-1 text-gray-700">{stringifyLocLeaf(r.description)}</div>
                                ) : null}
                              </span>
                            </label>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="mt-2 text-xs text-gray-500">
                        {t(
                          "complianceCenterUi.journey.apiRecommendationsEmpty",
                          "No API rows for this filter — curated hints above remain your deterministic fallback.",
                        )}
                      </p>
                    )}
                  </div>

                  <div className="mt-3 flex flex-col gap-2 border-t border-gray-100 pt-3">
                    <Button
                      type="button"
                      size="small"
                      color="primary"
                      className={`w-fit ${JOURNEY_PRIMARY_BTN}`}
                      disabled={
                        remediationBusy ||
                        !featureFlags.REMEDIATION_WORKFLOW_V2 ||
                        !featureFlags.RECOMMENDATION_LIFECYCLE_V2 ||
                        !selectedRecommendationId
                      }
                      onClick={() => void createRemediationFromJourney()}
                    >
                      {remediationBusy
                        ? t("complianceCenterUi.journey.remediationCreating", "Creating…")
                        : t("complianceCenterUi.journey.createRemediationTask", "Create remediation task")}
                    </Button>
                    {!featureFlags.REMEDIATION_WORKFLOW_V2 || !featureFlags.RECOMMENDATION_LIFECYCLE_V2 ? (
                      <p className="text-xs text-amber-800">
                        {t(
                          "complianceCenterUi.journey.remediationLifecycleHint",
                          "Remediation task creation requires REMEDIATION_WORKFLOW_V2 and persisted recommendations (RECOMMENDATION_LIFECYCLE_V2).",
                        )}
                      </p>
                    ) : null}
                    {remediationMessage ? <p className="text-xs text-gray-800">{remediationMessage}</p> : null}
                  </div>
                  </div>
                </details>
              </div>

              {journeyStandard !== "GDPR" && journeyStandard !== "NIS2" ? (
              <details className={ccStyles.legacyComplianceDetails}>
                <summary className={ccStyles.legacyComplianceSummary}>
                  {t("complianceCenterUi.journey.evidenceGuidanceTitle", "Evidence guidance")}
                </summary>
              <div id="cc-journey-evidence-guidance" className="mt-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                <p className="mt-1 text-xs text-gray-600">
                  {t(
                    "complianceCenterUi.journey.evidenceGuidanceSummary",
                    "Uploads stay in Evidence Hub — this journey only links record IDs so recompute can consume verification state.",
                  )}
                </p>
                <ul className="mt-2 list-inside list-disc space-y-1 text-xs text-gray-700">
                  <li>{t("complianceCenterUi.journey.evidenceRequired", "Required: primary control evidence per bootstrap (see catalog).")}</li>
                  <li>{t("complianceCenterUi.journey.evidenceStale", "Stale: refresh when past freshness SLA; projections may stay PARTIAL until renewed.")}</li>
                </ul>
                {selectedPackQ?.evidenceExpectations?.artifactTypes ? (
                  <p className="mt-2 text-xs text-gray-600">
                    {t("complianceCenterUi.journey.artifactTypes", "Suggested artifact types")}: {selectedPackQ.evidenceExpectations.artifactTypes.join(", ")}
                  </p>
                ) : null}
              </div>
              </details>
              ) : null}
              </>
              ) : null}
            </>
          )}
        </div>
      </div>

      {sessionModalOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="cc-journey-session-modal-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label={t("common.close", "Close")}
            onClick={() => setSessionModalOpen(false)}
          />
          <div
            className={`relative z-[101] flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-hidden ${listStyles.panel}`}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`flex items-start justify-between gap-2 border-b border-slate-100 ${listStyles.panelHeaderToolbarOnly}`}>
              <div className="min-w-0">
                <h4 id="cc-journey-session-modal-title" className={listStyles.panelExternalTitle}>
                  {controlCentricUxActive
                    ? t("controlCentric.session.modalTitle", "Execution session")
                    : t("complianceCenterUi.journey.sessionModalTitle", "Session setup")}
                </h4>
                <p className="mt-1 text-sm font-medium text-slate-800">{standardLabel(journeyStandard)}</p>
              </div>
              <Button type="button" size="small" color="outline" className="shrink-0" onClick={() => setSessionModalOpen(false)}>
                {t("common.close", "Close")}
              </Button>
            </div>
            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto">
              <article className={`${listStyles.panel} !shadow-none`}>
                <p className="text-xs font-semibold text-slate-900">
                  {t("complianceCenterUi.journey.sessionModalActionsTitle", "Create or refresh")}
                </p>
                <p className="mt-1 text-xs text-slate-600">
                  {t(
                    "complianceCenterUi.journey.sessionModalActionsBody",
                    "Start a new human workflow session for the selected standard, or reload the list from the server.",
                  )}
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button
                    type="button"
                    data-testid="journey-create-session"
                    size="small"
                    color="primary"
                    className={JOURNEY_PRIMARY_BTN}
                    onClick={() => void createSession()}
                    disabled={!standardAllowed || sessionsLoading}
                  >
                    {t("complianceCenterUi.journey.createSession", "Create session")}
                  </Button>
                  <Button
                    type="button"
                    data-testid="journey-refresh-sessions"
                    size="small"
                    color="outline"
                    onClick={() => void loadSessions()}
                    disabled={sessionsLoading}
                  >
                    {t("complianceCenterUi.journey.refreshSessions", "Refresh sessions")}
                  </Button>
                </div>
              </article>
              <article className={`${listStyles.panel} !shadow-none`}>
                <p className={listStyles.panelExternalTitle}>{t("complianceCenterUi.journey.resumeSession", "Resume session")}</p>
                <p className="mt-1 text-xs text-slate-600">
                  {t("complianceCenterUi.journey.sessionModalResumeBody", "Pick an existing session for this standard. Changing selection loads answers and projections.")}
                </p>
                {sessionsLoading ? (
                  <p className="mt-3 text-xs text-slate-600">{t("complianceCenterUi.journey.sessionsLoading", "Loading sessions…")}</p>
                ) : null}
                {sessionsListError ? <p className="mt-2 text-xs text-red-600">{sessionsListError}</p> : null}
                {!sessionsLoading && !sessionsListError && filteredSessionsForStandard.length === 0 ? (
                  <p className="mt-3 text-xs text-slate-600">{t("complianceCenterUi.journey.noSessionsInList", "No sessions yet for this standard. Create a session to begin.")}</p>
                ) : null}
                {sessionId ? (
                  <p className="mt-2 text-xs font-medium text-blue-900">
                    {t("complianceCenterUi.journey.selectedSessionBadge", "Current session")}:{" "}
                    {(() => {
                      const row = filteredSessionsForStandard.find((r: any) => String(r.id) === String(sessionId));
                      return row
                        ? formatHumanWorkflowSessionOptionLabel(row, standardLabel(String(row.standardCode || journeyStandard)))
                        : `${String(sessionId).slice(0, 8)}…`;
                    })()}
                  </p>
                ) : null}
                <label className="mt-3 block text-xs text-slate-700">
                  <span className="sr-only">{t("complianceCenterUi.journey.resumeSession", "Resume session")}</span>
                  <select
                    className="mt-1 w-full rounded border border-slate-300 bg-white px-2 py-2 text-sm text-slate-900"
                    value={sessionId || ""}
                    onChange={(e) => setSessionId(e.target.value || null)}
                    disabled={sessionsLoading}
                  >
                    <option value="">{t("complianceCenterUi.journey.noSessionSelected", "— none —")}</option>
                    {filteredSessionsForStandard.map((s) => (
                      <option key={s.id} value={s.id}>
                        {formatHumanWorkflowSessionOptionLabel(s, standardLabel(String(s.standardCode || journeyStandard)))}
                      </option>
                    ))}
                  </select>
                </label>
              </article>
              {sessionError ? <p className="text-xs text-red-600">{sessionError}</p> : null}
            </div>
          </div>
        </div>
      ) : null}

      {domainsModalOpen ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="cc-journey-domains-modal-title">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label={t("common.close", "Close")}
            onClick={() => setDomainsModalOpen(false)}
          />
          <div
            className="relative z-[101] flex max-h-[min(90vh,720px)] w-full max-w-lg flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-2 border-b border-slate-100 p-4">
              <div className="min-w-0">
                <h4 id="cc-journey-domains-modal-title" className="text-sm font-semibold text-slate-900">
                  {t("complianceCenterUi.journey.browseDomainsModalTitle", "Browse domains")}
                </h4>
                <p className="mt-1 text-xs text-slate-600">
                  {t("complianceCenterUi.journey.modalStandardLine", "Selected standard: {{standardName}}", {
                    standardName: standardLabel(journeyStandard),
                  })}
                </p>
              </div>
              <Button type="button" size="small" color="outline" className="shrink-0" onClick={() => setDomainsModalOpen(false)}>
                {t("common.close", "Close")}
              </Button>
            </div>
            <div className="border-b border-slate-100 px-4 pb-3">
              {journeyDomains.length > 10 ? (
                <input
                  type="search"
                  className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900"
                  value={domainModalQuery}
                  onChange={(e) => setDomainModalQuery(e.target.value)}
                  placeholder={t("complianceCenterUi.journey.domainSearchPlaceholder", "Search domains…")}
                  aria-label={t("complianceCenterUi.journey.domainSearchPlaceholder", "Search domains…")}
                />
              ) : null}
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto p-4">
              {journeyDomains.length === 0 ? (
                <p className="text-sm text-slate-600">{t("complianceCenterUi.journey.noDomainsInStandard", "No domains are available for this standard in the catalog.")}</p>
              ) : domainsModalFiltered.length === 0 ? (
                <p className="text-sm text-slate-600">{t("complianceCenterUi.journey.noDomainSearchResults", "No domains match your search.")}</p>
              ) : (
                <ul className="space-y-2 text-sm">
                  {domainsModalFiltered.map((d) => {
                    const st = domainStateMap.get(d.key);
                    const active = String(d.key) === String(selectedDomainKey);
                    return (
                      <li key={d.key}>
                        <button
                          type="button"
                          className={`w-full text-left ${ccStyles.questionPickCard} ${
                            active ? ccStyles.questionPickCardActive : ""
                          }`.trim()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDomainKey(String(d.key));
                            setSelectedQuestionKey(d.questions[0]?.questionKey || null);
                            setDomainsModalOpen(false);
                          }}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className={ccStyles.questionPickTitle}>{pickLoc(d.localization, lang)}</div>
                            {active ? (
                              <span className="shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-blue-900">
                                {t("complianceCenterUi.journey.domainModalSelectedHint", "Selected")}
                              </span>
                            ) : null}
                          </div>
                          <div className="mt-0.5 text-xs text-slate-600">
                            {t("complianceCenterUi.journey.progress", "Progress")}: {st?.progressPercent ?? 0}% ·{" "}
                            {t("complianceCenterUi.journey.readiness", "Readiness")}: {Math.round(Number(st?.readinessScore ?? 0))} ·{" "}
                            {t("complianceCenterUi.journey.blockers", "Blockers")}: {st?.blockerCount ?? 0}
                            {st?.details?.questionsTotal != null ? (
                              <>
                                {" "}
                                · {t("complianceCenterUi.journey.domainAnswered", "Answered")}: {st.details.questionsAnswered ?? 0}/{st.details.questionsTotal}
                              </>
                            ) : null}
                            {st?.evidenceCompleteness != null ? (
                              <>
                                {" "}
                                · {t("complianceCenterUi.journey.domainEvidenceCompleteness", "Evidence")}: {Math.round(Number(st.evidenceCompleteness))}%
                              </>
                            ) : null}
                          </div>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
